import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  VStack,
  HStack,
  Button,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  Badge,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
  Spinner,
  Image,
  Divider,
  Box,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw, FiEye, FiImage, FiPackage, FiDownload, FiSend, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { bpProductApi, bpProductNewApi, bpProductImageApi, bpProductRetailApi, packageApi, hospitalityApi } from '../config/api';
import { useAuth } from '../context/AuthContext';

// Mapping member code to display name
const MEMBER_CODE_MAPPING = {
  'api': 'Injourney Airports',
  'ias': 'Injourney Aviation Services',
  'twc': 'Injourney Destination Management',
  'hin': 'Injourney Hospitality',
  'itdc': 'Injourney Tourism Development',
  'srn': 'Injourney Retail'
};

const getFriendlyApiMessage = (error, fallback) => {
  const rawMessage = error?.response?.data?.message || error?.message || '';
  const message = String(rawMessage);
  const looksTechnical =
    message.includes('Error at Query.run') ||
    message.includes('node_modules') ||
    message.includes('\\') ||
    message.includes('\n');

  return looksTechnical ? fallback : message || fallback;
};

const BundlingProductTab = ({ memberId = null, importedProduct = null, onImportedProductConsumed }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hinHotels, setHinHotels] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [importedSourceProduct, setImportedSourceProduct] = useState(null);

  // Package display states
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [showPackages, setShowPackages] = useState(false);

  // Product Image states
  const [managingImagesProduct, setManagingImagesProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteImage, setDeleteImage] = useState(null);

  // Product Stock states
  const [managingStocksProduct, setManagingStocksProduct] = useState(null);
  const [productStocks, setProductStocks] = useState([]);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [deleteStock, setDeleteStock] = useState(null);

  // Variant states
  const [managingVariantsProduct, setManagingVariantsProduct] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [deleteVariant, setDeleteVariant] = useState(null);
  const [variantFormData, setVariantFormData] = useState({
    BPPRODUCTVARIANT_KEY: '',
    BPPRODUCTVARIANT_NAME: '',
    BPPRODUCTVARIANT_BARCODE: '',
    BPPRODUCTVARIANT_SKU: '',
    BPPRODUCTVARIANT_BASE64: '',
    BPPRODUCT_KEY: '',
  });

  // Attribute states
  const [managingAttributesVariant, setManagingAttributesVariant] = useState(null);
  const [variantAttributes, setVariantAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [deleteAttribute, setDeleteAttribute] = useState(null);
  const [attributeFormData, setAttributeFormData] = useState({
    BPPRODUCTATTR_ATTRIBUTE: '',
    BPPRODUCTATTR_VALUE: '',
    BPPRODUCTATTR_DISPLAYNAME: '',
    BPPRODUCTVARIANT_KEY: '',
  });

  const toast = useToast();
  const { user } = useAuth();
  const isMobileView = useBreakpointValue({ base: true, md: false });

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isImagesOpen, onOpen: onImagesOpen, onClose: onImagesClose } = useDisclosure();
  const { isOpen: isDeleteImageOpen, onOpen: onDeleteImageOpen, onClose: onDeleteImageClose } = useDisclosure();
  const { isOpen: isStocksOpen, onOpen: onStocksOpen, onClose: onStocksClose } = useDisclosure();
  const { isOpen: isStockFormOpen, onOpen: onStockFormOpen, onClose: onStockFormClose } = useDisclosure();
  const { isOpen: isDeleteStockOpen, onOpen: onDeleteStockOpen, onClose: onDeleteStockClose } = useDisclosure();
  const { isOpen: isVariantsOpen, onOpen: onVariantsOpen, onClose: onVariantsClose } = useDisclosure();
  const { isOpen: isVariantFormOpen, onOpen: onVariantFormOpen, onClose: onVariantFormClose } = useDisclosure();
  const { isOpen: isDeleteVariantOpen, onOpen: onDeleteVariantOpen, onClose: onDeleteVariantClose } = useDisclosure();
  const { isOpen: isAttributesOpen, onOpen: onAttributesOpen, onClose: onAttributesClose } = useDisclosure();
  const { isOpen: isAttributeFormOpen, onOpen: onAttributeFormOpen, onClose: onAttributeFormClose } = useDisclosure();
  const { isOpen: isDeleteAttributeOpen, onOpen: onDeleteAttributeOpen, onClose: onDeleteAttributeClose } = useDisclosure();

  const cancelRef = React.useRef();
  const cancelImageRef = React.useRef();
  const cancelStockRef = React.useRef();
  const cancelVariantRef = React.useRef();
  const cancelAttributeRef = React.useRef();
  const importedProductKeyRef = React.useRef(null);

  // ponytail: extract location string from BPPRODUCT_META JSON
  const productLocation = (meta) => {
    if (!meta) return '';
    try {
      const obj = typeof meta === 'string' ? JSON.parse(meta) : meta;
      return obj?.location || '';
    } catch (_) { return ''; }
  };

  // ponytail: extract price includes/excludes strings from BPPRODUCT_META JSON
  const productMetaField = (meta, key) => {
    if (!meta) return '';
    try {
      const obj = typeof meta === 'string' ? JSON.parse(meta) : meta;
      return obj?.[key] || '';
    } catch (_) { return ''; }
  };

  // IAS Joumpa products use this catalog category (supplierRegistry → joumpa adapter).
  // Legacy rows may still say "Joumpa Service" — treat both as Joumpa for the flt_type radio.
  const isJoumpaCategory = (cat) =>
    cat === 'Concierge / Passanger Services' || cat === 'Joumpa Service';

  // Check if this is for Injourney Retail (srn) - use retail API structure
  const isRetailProduct = memberId === 'srn';

  const [formData, setFormData] = useState({
    ...(isRetailProduct ? {
      BPPRODUCT_NAME: '',
      BPPRODUCT_KEY: '',
      BPPRODUCT_STATUS: '1',
      BPPRODUCT_CATEGORY: '',
    } : {
      BPPRODUCT_CODE: '',
      BPPRODUCT_NAME: '',
      BPPRODUCT_DESC: '',
      MEMBER_CODE: memberId || '',
      BPPRODUCT_PUBLISHPRICE: '',
      BPPRODUCT_CORPPRICE: '',
      BPPRODUCT_AGENTPRICE: '',
      BPPRODUCT_QUANTITY: '',
      BPPRODUCT_UNIT: '',
      BPPRODUCT_CATEGORY: '',
      BPPRODUCT_ASSETSERVICEOWNER: '',
      BPPRODUCT_META: '',
      LOCATION: '',
      BOOKING_CATEGORY: '', HOTEL_CODE: '', ROOM_TYPE: '', ROOM_QTY: '',
      PRICE_INCLUDES: '',
      PRICE_EXCLUDES: '',
      FLT_TYPE: '',
      BPPRODUCT_STARTDATE: '',
      BPPRODUCT_ENDDATE: '',
    }),
  });

  const [syncing, setSyncing] = useState(false);

  // Daftar hotel dari Hotel Master, dimuat sekali saat kategori Reservation dipilih.
  const isReservation = formData.BOOKING_CATEGORY === 'reservation';
  useEffect(() => {
    if (!isReservation || hinHotels.length) return;
    hospitalityApi.get('/hin/hotels', { params: { limit: 200 } })
      .then((res) => setHinHotels(res.data?.data || []))
      .catch((err) => console.warn('Gagal memuat Hotel Master:', err.message));
  }, [isReservation, hinHotels.length]);

  const [stockFormData, setStockFormData] = useState({
    BPPRODUCTSTOCKS_SKU: '',
    BPPRODUCTSTOCKS_LOCATIONID: '',
    BPPRODUCTSTOCKS_LOCATIONNAME: '',
    BPPRODUCTSTOCKS_STOCK: '',
    BPPRODUCTSTOCKS_SELLPRICE: '',
    BPPRODUCTSTOCKS_PRICELISTNAME: '',
    BPPRODUCTVARIANT_KEY: '',
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // Use different API for retail products vs non-retail
      let response;
      if (isRetailProduct) {
        response = await bpProductRetailApi.get('/list');
      } else {
        // Use new API for non-retail products
        const params = memberId ? { member: memberId } : {};
        response = await bpProductNewApi.get('/list', { params });
      }

      if (response.data.status === 200) {
        setProducts(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: getFriendlyApiMessage({ message: response.data.message }, 'Gagal mengambil data product'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: 'Error',
        description: getFriendlyApiMessage(error, 'Terjadi kesalahan saat mengambil data product'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, memberId, isRetailProduct]);

  const fetchPackages = useCallback(async () => {
    if (!memberId || isRetailProduct) return;
    setLoadingPackages(true);
    try {
      const res = await packageApi.get('/list', { params: { pic_member: memberId } });
      if (res.data?.status === 200) {
        setPackages((res.data.data || []).map(p => ({
          ...p,
          PACKAGE_NAME: p.PACKAGE_NAME || p.PACKAGE_CODE || '-',
        })));
      }
    } catch (e) {
      console.error('Error fetching packages:', e);
    } finally {
      setLoadingPackages(false);
    }
  }, [memberId, isRetailProduct]);

  useEffect(() => {
    fetchProducts();
    fetchPackages();
  }, [fetchProducts, fetchPackages]);

  // Format number to Rupiah
  const formatRupiah = useCallback((value) => {
    if (!value) return '';
    // Remove non-numeric characters
    const numericValue = value.toString().replace(/\D/g, '');
    if (!numericValue) return '';
    // Format with thousand separators using dot
    return numericValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }, []);

  // Parse Rupiah format to number (remove formatting)
  const parseRupiah = useCallback((value) => {
    if (!value) return '';
    // Remove all non-numeric characters
    return value.toString().replace(/\D/g, '');
  }, []);

  const buildImportedProductDescription = useCallback((sourceProduct) => {
    const sourceLabel = sourceProduct?.sourceLabel || 'IDM API';
    const externalCode = sourceProduct?.code || sourceProduct?.meta?.branch_code || '-';
    const lines = [
      `Imported from ${sourceLabel}.`,
      `External ID: ${sourceProduct?.id ?? '-'}`,
      `External Code: ${externalCode}`,
      `Currency: ${sourceProduct?.currency || 'IDR'}`,
      '',
      'Lengkapi deskripsi marketing, kategori, gambar, dan readiness sebelum produk digunakan untuk bundling.',
    ];
    return lines.join('\n');
  }, []);

  useEffect(() => {
    const importKey = importedProduct?.importedAt || importedProduct?.id || importedProduct?.code;
    if (!importedProduct || !importKey || importedProductKeyRef.current === importKey || isRetailProduct) {
      return;
    }

    const openImportedProductForm = async () => {
      importedProductKeyRef.current = importKey;
      setEditingProduct(null);
      setImportedSourceProduct(importedProduct);

      let productCode = '';
      if (memberId) {
        try {
          const response = await bpProductNewApi.get('/generate-code', {
            params: { memberCode: memberId },
          });
          if (response.data.status === 200) {
            productCode = response.data.data.productCode;
          }
        } catch (error) {
          console.error('Error generating product code for imported source product:', error);
        }
      }

      const price = importedProduct?.price != null ? formatRupiah(importedProduct.price) : '';
      const sourceLabel = importedProduct?.sourceLabel || 'IDM API';

      // Auto-map IAS source → category (harus match dengan supplierRegistry)
      const SOURCE_CATEGORY_MAP = {
        bss: 'Baggage Wrapping',
        // Same category admins pick for IAS Joumpa (supplierRegistry → joumpa adapter).
        joumpa: 'Concierge / Passanger Services',
        lounge: 'Airport Lounge',
      };
      const autoCategory = SOURCE_CATEGORY_MAP[importedProduct?.source] || '';

      // Persist the supplier binding a hand-typed product lacks. At dispatch the
      // backend parses this into component.metadata so the IAS adapter builds a
      // real request (Joumpa route/product_id, Lounge unit_code, BSS branch).
      const buildProductMeta = (p) => {
        const src = p?.source;
        if (src === 'joumpa') {
          return {
            source: 'joumpa',
            product_id: p?.id ?? p?.meta?.product_id ?? p?.meta?.id ?? null,
            rate_id: p?.rate_id ?? p?.meta?.rate_id ?? null,
            airport: p?.airport ?? null,
            flt_type: p?.flt_type ?? null,
            airport_origin: p?.airport_origin ?? null,
            airport_destination: p?.airport_destination ?? null,
          };
        }
        if (src === 'lounge') {
          return {
            source: 'lounge',
            unit_code: p?.code ?? p?.meta?.unit_code ?? null,
            branch: p?.branch ?? p?.meta?.branch ?? p?.meta?.terminal ?? null,
          };
        }
        if (src === 'bss') {
          return {
            source: 'bss',
            branch: p?.code ?? p?.meta?.branch_code ?? null,
            dom_int: p?.meta?.dom_int ?? null,
          };
        }
        return {};
      };
      const productMeta = buildProductMeta(importedProduct);

      setFormData({
        BPPRODUCT_CODE: productCode,
        BPPRODUCT_NAME: importedProduct?.name || '',
        BPPRODUCT_DESC: buildImportedProductDescription(importedProduct),
        MEMBER_CODE: memberId || '',
        BPPRODUCT_PUBLISHPRICE: price,
        BPPRODUCT_CORPPRICE: '',
        BPPRODUCT_AGENTPRICE: '',
        BPPRODUCT_QUANTITY: '1',
        BPPRODUCT_UNIT: 'Ticket',
        BPPRODUCT_CATEGORY: autoCategory,
        BPPRODUCT_ASSETSERVICEOWNER: sourceLabel,
        BPPRODUCT_META: Object.keys(productMeta).length ? JSON.stringify(productMeta) : '',
        LOCATION: '',
        BOOKING_CATEGORY: '', HOTEL_CODE: '', ROOM_TYPE: '', ROOM_QTY: '',
        PRICE_INCLUDES: '',
        PRICE_EXCLUDES: '',
        FLT_TYPE: productMeta.flt_type || '',
        BPPRODUCT_STARTDATE: '',
        BPPRODUCT_ENDDATE: '',
      });

      onFormOpen();
      if (typeof onImportedProductConsumed === 'function') {
        onImportedProductConsumed();
      }
    };

    openImportedProductForm();
  }, [
    buildImportedProductDescription,
    formatRupiah,
    importedProduct,
    isRetailProduct,
    memberId,
    onFormOpen,
    onImportedProductConsumed,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle price fields with Rupiah format
    if (name === 'BPPRODUCT_PUBLISHPRICE' || name === 'BPPRODUCT_CORPPRICE' || name === 'BPPRODUCT_AGENTPRICE') {
      // Store formatted value for display
      const formattedValue = formatRupiah(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleStockInputChange = (e) => {
    const { name, value } = e.target;
    setStockFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = async () => {
    setEditingProduct(null);
    setImportedSourceProduct(null);

    // Auto-generate product code for non-retail products
    let productCode = '';
    if (!isRetailProduct && memberId) {
      try {
        const response = await bpProductNewApi.get('/generate-code', {
          params: { memberCode: memberId }
        });
        if (response.data.status === 200) {
          productCode = response.data.data.productCode;
        }
      } catch (error) {
        console.error('Error generating product code:', error);
        // Continue with empty code if generation fails
      }
    }

    setFormData({
      ...(isRetailProduct ? {
        BPPRODUCT_NAME: '',
        BPPRODUCT_KEY: '',
        BPPRODUCT_CATEGORY: '',
        BPPRODUCT_STATUS: '1',
      } : {
        BPPRODUCT_CODE: productCode,
        BPPRODUCT_NAME: '',
        BPPRODUCT_DESC: '',
        MEMBER_CODE: memberId || '',
        BPPRODUCT_PUBLISHPRICE: '',
        BPPRODUCT_CORPPRICE: '',
        BPPRODUCT_AGENTPRICE: '',
        BPPRODUCT_QUANTITY: '',
        BPPRODUCT_UNIT: '',
        BPPRODUCT_CATEGORY: '',
        BPPRODUCT_ASSETSERVICEOWNER: '',
        BPPRODUCT_META: '',
        LOCATION: '',
        BOOKING_CATEGORY: '', HOTEL_CODE: '', ROOM_TYPE: '', ROOM_QTY: '',
        PRICE_INCLUDES: '',
        PRICE_EXCLUDES: '',
        FLT_TYPE: '',
        BPPRODUCT_STARTDATE: '',
        BPPRODUCT_ENDDATE: '',
      }),
    });
    onFormOpen();
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setImportedSourceProduct(null);
    setFormData({
      ...(isRetailProduct ? {
        BPPRODUCT_NAME: product.BPPRODUCT_NAME || '',
        BPPRODUCT_KEY: product.BPPRODUCT_KEY || '',
        BPPRODUCT_CATEGORY: product.BPPRODUCT_CATEGORY || '',
        BPPRODUCT_STATUS: product.BPPRODUCT_STATUS?.toString() || '1',
      } : {
        BPPRODUCT_CODE: product.BPPRODUCT_CODE || '',
        BPPRODUCT_NAME: product.BPPRODUCT_NAME || '',
        BPPRODUCT_DESC: product.BPPRODUCT_DESC || '',
        MEMBER_CODE: product.MEMBER_CODE || memberId || '',
        BPPRODUCT_PUBLISHPRICE: product.BPPRODUCT_PUBLISHPRICE ? formatRupiah(product.BPPRODUCT_PUBLISHPRICE) : '',
        BPPRODUCT_CORPPRICE: product.BPPRODUCT_CORPPRICE ? formatRupiah(product.BPPRODUCT_CORPPRICE) : '',
        BPPRODUCT_AGENTPRICE: product.BPPRODUCT_AGENTPRICE ? formatRupiah(product.BPPRODUCT_AGENTPRICE) : '',
        BPPRODUCT_QUANTITY: product.BPPRODUCT_QUANTITY || '',
        BPPRODUCT_UNIT: product.BPPRODUCT_UNIT || '',
        BPPRODUCT_CATEGORY: product.BPPRODUCT_CATEGORY || '',
        BPPRODUCT_ASSETSERVICEOWNER: product.BPPRODUCT_ASSETSERVICEOWNER || '',
        BPPRODUCT_META: product.BPPRODUCT_META || '',
        LOCATION: productLocation(product.BPPRODUCT_META),
        BOOKING_CATEGORY: productMetaField(product.BPPRODUCT_META, 'bookingCategory'),
        HOTEL_CODE: productMetaField(product.BPPRODUCT_META, 'hotelCode'),
        ROOM_TYPE: ['roomTypeId', 'room_type_id', 'roomTypeCode', 'room_type_code', 'roomName', 'room_name', 'roomType', 'room_type'].map(key => productMetaField(product.BPPRODUCT_META, key)).find(Boolean) || '',
        ROOM_QTY: productMetaField(product.BPPRODUCT_META, 'roomQty') || productMetaField(product.BPPRODUCT_META, 'room_qty') || '',
        PRICE_INCLUDES: productMetaField(product.BPPRODUCT_META, 'priceIncludes'),
        PRICE_EXCLUDES: productMetaField(product.BPPRODUCT_META, 'priceExcludes'),
        FLT_TYPE: productMetaField(product.BPPRODUCT_META, 'flt_type'),
        BPPRODUCT_STARTDATE: product.BPPRODUCT_STARTDATE ? product.BPPRODUCT_STARTDATE.split('T')[0] : '',
        BPPRODUCT_ENDDATE: product.BPPRODUCT_ENDDATE ? product.BPPRODUCT_ENDDATE.split('T')[0] : '',
      }),
    });
    onFormOpen();
  };

  const handleSubmit = async () => {
    if (formData.BOOKING_CATEGORY === 'reservation' && (
      !formData.HOTEL_CODE || !String(formData.ROOM_TYPE || '').trim() ||
      !Number.isInteger(Number(formData.ROOM_QTY)) || Number(formData.ROOM_QTY) < 1
    )) {
      toast({ title: 'Lengkapi data reservasi', description: 'Pilih hotel, isi tipe kamar dan jumlah kamar minimal 1.', status: 'warning' });
      return;
    }

    if (!formData.BPPRODUCT_NAME) {
      toast({
        title: 'Validasi gagal',
        description: 'Nama product harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (isRetailProduct && !formData.BPPRODUCT_KEY) {
      toast({
        title: 'Validasi gagal',
        description: 'Product Key harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!isRetailProduct && isJoumpaCategory(formData.BPPRODUCT_CATEGORY) && !formData.FLT_TYPE) {
      toast({
        title: 'Validasi gagal',
        description: 'Untuk Concierge / Joumpa, pilih tipe: Arrival, Departure, atau Transfer',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      let response;

      if (isRetailProduct) {
        // Use retail API
        const submitData = {
          BPPRODUCT_NAME: formData.BPPRODUCT_NAME,
          BPPRODUCT_KEY: formData.BPPRODUCT_KEY,
          BPPRODUCT_STATUS: formData.BPPRODUCT_STATUS,
          BPPRODUCT_CATEGORY: formData.BPPRODUCT_CATEGORY || null,
        };

        if (editingProduct) {
          response = await bpProductRetailApi.post(`/update/${editingProduct.BPPRODUCT_ID}`, submitData);
        } else {
          response = await bpProductRetailApi.post('/', submitData);
        }
      } else {
        // Use new API for non-retail products
        const submitData = {
          BPPRODUCT_CODE: formData.BPPRODUCT_CODE || null,
          BPPRODUCT_NAME: formData.BPPRODUCT_NAME,
          BPPRODUCT_DESC: formData.BPPRODUCT_DESC || null,
          MEMBER_CODE: memberId || null, // Always use memberId from props, not from formData
          BPPRODUCT_PUBLISHPRICE: parseRupiah(formData.BPPRODUCT_PUBLISHPRICE) || null,
          BPPRODUCT_CORPPRICE: parseRupiah(formData.BPPRODUCT_CORPPRICE) || null,
          BPPRODUCT_AGENTPRICE: parseRupiah(formData.BPPRODUCT_AGENTPRICE) || null,
          BPPRODUCT_QUANTITY: formData.BPPRODUCT_QUANTITY || null,
          BPPRODUCT_UNIT: formData.BPPRODUCT_UNIT || null,
          BPPRODUCT_CATEGORY: formData.BPPRODUCT_CATEGORY || null,
          BPPRODUCT_ASSETSERVICEOWNER: formData.BPPRODUCT_ASSETSERVICEOWNER || null,
          BPPRODUCT_META: (() => {
            let meta = {};
            try { if (formData.BPPRODUCT_META) meta = typeof formData.BPPRODUCT_META === 'string' ? JSON.parse(formData.BPPRODUCT_META) : formData.BPPRODUCT_META; } catch (_) { }
            meta.location = formData.LOCATION || null;
            // Reservation = ketersediaan harus dikonfirmasi ke supplier (hotel/HIN)
            // sebelum pembayaran dibuka. Kosong = ikut default backend (Payment,
            // kecuali kategori produk memang hotel).
            meta.bookingCategory = formData.BOOKING_CATEGORY || null;
            // Email reservasi dikirim ke HOTEL_EMAIL hotel ini di Hotel Master.
            meta.hotelCode = formData.BOOKING_CATEGORY === 'reservation' ? (formData.HOTEL_CODE || null) : null;
            if (formData.BOOKING_CATEGORY === 'reservation') {
              for (const key of ['roomTypeId', 'room_type_id', 'roomTypeCode', 'room_type_code', 'roomName', 'room_name', 'roomType', 'room_type']) delete meta[key];
              meta.roomType = String(formData.ROOM_TYPE).trim();
              meta.roomQty = Number(formData.ROOM_QTY);
            }
            meta.priceIncludes = formData.PRICE_INCLUDES || null;
            meta.priceExcludes = formData.PRICE_EXCLUDES || null;
            if (formData.HOTEL_CODE) {
              meta.hotelCode = String(formData.HOTEL_CODE).trim().toUpperCase();
            } else {
              delete meta.hotelCode;
              delete meta.hotel_code;
            }
            if (isJoumpaCategory(formData.BPPRODUCT_CATEGORY)) {
              meta.flt_type = formData.FLT_TYPE || null;
            }
            return JSON.stringify(meta);
          })(),
          BPPRODUCT_STARTDATE: formData.BPPRODUCT_STARTDATE || null,
          BPPRODUCT_ENDDATE: formData.BPPRODUCT_ENDDATE || null,
        };

        if (editingProduct) {
          response = await bpProductNewApi.post(`/update/${editingProduct.BPPRODUCT_ID}`, submitData);
        } else {
          response = await bpProductNewApi.post('/', submitData);
        }
      }

      if (response.data.status === 200 || response.data.status === 201) {
        toast({
          title: 'Berhasil',
          description: editingProduct ? 'Product berhasil diupdate' : 'Product berhasil dibuat',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onFormClose();
        setImportedSourceProduct(null);
        fetchProducts();
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleView = async (product) => {
    setViewingProduct(product);
    // For retail products, fetch full product details with variants
    if (isRetailProduct) {
      try {
        const response = await bpProductRetailApi.get(`/${product.BPPRODUCT_ID}`);
        if (response.data.status === 200) {
          setViewingProduct(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
      }
    } else {
      // Fetch images for non-retail products using new API
      try {
        const imagesRes = await bpProductImageApi.get(`/product/${product.BPPRODUCT_ID}`);
        setViewingProduct({
          ...product,
          images: imagesRes.data.data || []
        });
      } catch (error) {
        console.error('Error fetching product details:', error);
      }
    }
    onViewOpen();
  };

  const handleDeleteClick = (product) => {
    setDeleteProduct(product);
    onDeleteOpen();
  };

  const handlePublishToggle = async (product) => {
    const api = isRetailProduct ? bpProductRetailApi : bpProductNewApi;
    try {
      if (product.BPPRODUCT_APISTATUS === 'published') {
        const res = await api.post(`/unpublish/${product.BPPRODUCT_ID}`);
        if (res.data?.status === 200) {
          toast({ title: 'Unpublished', description: `"${product.BPPRODUCT_NAME}" ditarik dari API Hub.`, status: 'info', duration: 3000, isClosable: true });
          fetchProducts();
        }
      } else {
        const res = await api.post(`/publish/${product.BPPRODUCT_ID}`);
        if (res.data?.status === 200) {
          toast({ title: 'Published!', description: res.data.message || `"${product.BPPRODUCT_NAME}" tersedia di API Hub.`, status: 'success', duration: 4000, isClosable: true });
          fetchProducts();
        }
      }
    } catch (err) {
      toast({ title: 'Gagal', description: err.response?.data?.message || err.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteProduct) return;

    try {
      // Use different API for retail vs non-retail
      const api = isRetailProduct ? bpProductRetailApi : bpProductNewApi;
      const response = await api.post(`/delete/${deleteProduct.BPPRODUCT_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: response.data.message || 'Product berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteProduct(null);
        fetchProducts();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Product Image handlers
  const handleManageImages = async (product) => {
    setManagingImagesProduct(product);
    onImagesOpen();
    await fetchProductImages(product.BPPRODUCT_ID);
  };

  const fetchProductImages = async (productId) => {
    setLoadingImages(true);
    try {
      let response;
      if (isRetailProduct) {
        // For retail, get images from variants (BPPRODUCTVARIANT_BASE64)
        response = await bpProductRetailApi.get(`/${productId}/images`);
        if (response.data.status === 200) {
          setProductImages(response.data.data || []);
        }
      } else {
        // For non-retail, use new API
        response = await bpProductImageApi.get(`/product/${productId}`);
        if (response.data.status === 200) {
          setProductImages(response.data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching product images:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data gambar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setProductImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageUpload = async (e) => {
    if (isRetailProduct) {
      toast({
        title: 'Info',
        description: 'Untuk retail products, gambar dikelola melalui variants. Silakan edit variant untuk mengelola gambar.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !managingImagesProduct) return;

    const invalidFiles = [];
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        invalidFiles.push({ name: file.name, reason: 'File harus berupa gambar' });
      } else if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push({ name: file.name, reason: 'Ukuran file maksimal 5MB' });
      }
    });

    if (invalidFiles.length > 0) {
      toast({
        title: 'Error Validasi',
        description: `${invalidFiles.length} file tidak valid`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      e.target.value = '';
      return;
    }

    const validFiles = files.filter(file =>
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
    );

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('BPPRODUCT_ID', managingImagesProduct.BPPRODUCT_ID);

      // Use new API for non-retail products
      const response = await bpProductImageApi.post('/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 201) {
        const uploadedCount = response.data.data?.length || 0;
        toast({
          title: 'Berhasil',
          description: `Berhasil mengupload ${uploadedCount} gambar`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await fetchProductImages(managingImagesProduct.BPPRODUCT_ID);
        e.target.value = '';
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengupload gambar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImageClick = (image) => {
    setDeleteImage(image);
    onDeleteImageOpen();
  };

  const handleDeleteImageConfirm = async () => {
    if (!deleteImage || !managingImagesProduct) return;

    try {
      // Use new API for non-retail products
      const response = await bpProductImageApi.post(`/delete/${deleteImage.BPPRODUCTIMAGE_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Gambar berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteImageClose();
        setDeleteImage(null);
        await fetchProductImages(managingImagesProduct.BPPRODUCT_ID);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus gambar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Product Stock handlers
  const handleManageStocks = async (product) => {
    setManagingStocksProduct(product);
    onStocksOpen();
    if (isRetailProduct) {
      // For retail, use product ID
      if (product.BPPRODUCT_ID) {
        await fetchProductStocks(product.BPPRODUCT_ID);
        // Also fetch variants for stock form
        await fetchProductVariants(product.BPPRODUCT_ID);
      }
    } else {
      // For non-retail, use SKU
      if (product.BPPRODUCT_SKU) {
        await fetchProductStocks(product.BPPRODUCT_SKU);
      }
    }
  };

  const fetchProductStocks = async (identifier) => {
    setLoadingStocks(true);
    try {
      let response;
      if (isRetailProduct) {
        // For retail, use product ID to get stocks from all variants
        const api = bpProductRetailApi;
        response = await api.get(`/${identifier}/stocks`);
        if (response.data.status === 200) {
          setProductStocks(response.data.data || []);
        }
      } else {
        // For non-retail, use SKU
        response = await bpProductApi.get(`/stocks/sku/${identifier}`);
        if (response.data.status === 200) {
          setProductStocks(response.data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching product stocks:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data stock',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setProductStocks([]);
    } finally {
      setLoadingStocks(false);
    }
  };

  const handleCreateStock = (variantKey = null) => {
    if (isRetailProduct) {
      // For retail, we need variant key
      if (!variantKey && !managingStocksProduct) {
        toast({
          title: 'Error',
          description: 'Variant key harus diisi',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setEditingStock(null);
      setStockFormData({
        BPPRODUCTSTOCKS_SKU: '',
        BPPRODUCTSTOCKS_LOCATIONID: '',
        BPPRODUCTSTOCKS_LOCATIONNAME: '',
        BPPRODUCTSTOCKS_STOCK: '',
        BPPRODUCTSTOCKS_SELLPRICE: '',
        BPPRODUCTSTOCKS_PRICELISTNAME: '',
        BPPRODUCTVARIANT_KEY: variantKey || '',
      });
      onStockFormOpen();
      return;
    }

    if (!managingStocksProduct || !managingStocksProduct.BPPRODUCT_SKU) {
      toast({
        title: 'Error',
        description: 'Product harus memiliki SKU terlebih dahulu',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingStock(null);
    setStockFormData({
      BPPRODUCTSTOCKS_SKU: managingStocksProduct.BPPRODUCT_SKU,
      BPPRODUCTSTOCKS_LOCATIONID: '',
      BPPRODUCTSTOCKS_LOCATIONNAME: '',
      BPPRODUCTSTOCKS_STOCK: '',
      BPPRODUCTSTOCKS_SELLPRICE: '',
      BPPRODUCTSTOCKS_PRICELISTNAME: '',
      BPPRODUCTVARIANT_KEY: '',
    });
    onStockFormOpen();
  };

  const handleEditStock = (stock) => {
    setEditingStock(stock);
    setStockFormData({
      BPPRODUCTSTOCKS_SKU: stock.BPPRODUCTSTOCKS_SKU || '',
      BPPRODUCTSTOCKS_LOCATIONID: stock.BPPRODUCTSTOCKS_LOCATIONID || '',
      BPPRODUCTSTOCKS_LOCATIONNAME: stock.BPPRODUCTSTOCKS_LOCATIONNAME || '',
      BPPRODUCTSTOCKS_STOCK: stock.BPPRODUCTSTOCKS_STOCK || '',
      BPPRODUCTSTOCKS_SELLPRICE: stock.BPPRODUCTSTOCKS_SELLPRICE || '',
      BPPRODUCTSTOCKS_PRICELISTNAME: stock.BPPRODUCTSTOCKS_PRICELISTNAME || '',
      BPPRODUCTVARIANT_KEY: stock.BPPRODUCTVARIANT_KEY || '',
    });
    onStockFormOpen();
  };

  const handleStockSubmit = async () => {
    if (isRetailProduct) {
      // For retail, validate variant key
      if (!stockFormData.BPPRODUCTVARIANT_KEY) {
        toast({
          title: 'Validasi gagal',
          description: 'Variant Key harus diisi',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      try {
        if (editingStock) {
          const response = await bpProductRetailApi.post(`/stock/update/${editingStock.BPPRODUCTSTOCKS_ID}`, {
            BPPRODUCTSTOCKS_LOCATIONID: stockFormData.BPPRODUCTSTOCKS_LOCATIONID,
            BPPRODUCTSTOCKS_LOCATIONNAME: stockFormData.BPPRODUCTSTOCKS_LOCATIONNAME,
            BPPRODUCTSTOCKS_STOCK: stockFormData.BPPRODUCTSTOCKS_STOCK,
            BPPRODUCTSTOCKS_SELLPRICE: stockFormData.BPPRODUCTSTOCKS_SELLPRICE,
            BPPRODUCTSTOCKS_PRICELISTNAME: stockFormData.BPPRODUCTSTOCKS_PRICELISTNAME,
          });
          if (response.data.status === 200) {
            toast({
              title: 'Berhasil',
              description: 'Stock berhasil diupdate',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            onStockFormClose();
            if (managingStocksProduct && managingStocksProduct.BPPRODUCT_ID) {
              await fetchProductStocks(managingStocksProduct.BPPRODUCT_ID);
            }
          }
        } else {
          const response = await bpProductRetailApi.post('/stock', {
            ...stockFormData,
            CREATED_BY: user?.username || 'system'
          });
          if (response.data.status === 201 || response.data.status === 200) {
            toast({
              title: 'Berhasil',
              description: 'Stock berhasil dibuat',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            onStockFormClose();
            if (managingStocksProduct && managingStocksProduct.BPPRODUCT_ID) {
              await fetchProductStocks(managingStocksProduct.BPPRODUCT_ID);
            }
          }
        }
      } catch (error) {
        console.error('Error saving stock:', error);
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      return;
    }

    if (!stockFormData.BPPRODUCTSTOCKS_SKU) {
      toast({
        title: 'Validasi gagal',
        description: 'SKU harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (editingStock) {
        const response = await bpProductApi.post(`/stocks/update/${editingStock.BPPRODUCTSTOCKS_ID}`, stockFormData);
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Stock berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onStockFormClose();
          await fetchProductStocks(stockFormData.BPPRODUCTSTOCKS_SKU);
        }
      } else {
        const response = await bpProductApi.post('/stocks', stockFormData);
        if (response.data.status === 201 || response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Stock berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onStockFormClose();
          await fetchProductStocks(stockFormData.BPPRODUCTSTOCKS_SKU);
        }
      }
    } catch (error) {
      console.error('Error saving stock:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteStockClick = (stock) => {
    setDeleteStock(stock);
    onDeleteStockOpen();
  };

  const handleDeleteStockConfirm = async () => {
    if (!deleteStock) return;

    try {
      if (isRetailProduct) {
        // For retail, stock management is done through variants
        // Note: We need to implement delete stock endpoint for retail
        // For now, show info message
        toast({
          title: 'Info',
          description: 'Untuk retail products, stock dikelola melalui variants. Silakan edit variant untuk menghapus stock.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        onDeleteStockClose();
        setDeleteStock(null);
        return;
      }

      const response = await bpProductApi.post(`/stocks/delete/${deleteStock.BPPRODUCTSTOCKS_ID}`);
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Stock berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteStockClose();
        setDeleteStock(null);
        if (managingStocksProduct) {
          if (isRetailProduct && managingStocksProduct.BPPRODUCT_ID) {
            await fetchProductStocks(managingStocksProduct.BPPRODUCT_ID);
          } else if (managingStocksProduct.BPPRODUCT_SKU) {
            await fetchProductStocks(managingStocksProduct.BPPRODUCT_SKU);
          }
        }
      }
    } catch (error) {
      console.error('Error deleting stock:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ========== VARIANT HANDLERS ==========

  const handleManageVariants = async (product) => {
    setManagingVariantsProduct(product);
    onVariantsOpen();
    await fetchProductVariants(product.BPPRODUCT_ID);
  };

  const fetchProductVariants = async (productId) => {
    setLoadingVariants(true);
    try {
      const response = await bpProductRetailApi.get(`/${productId}/variants`);
      if (response.data.status === 200) {
        setProductVariants(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching product variants:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data variants',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingVariants(false);
    }
  };

  const handleCreateVariant = () => {
    if (!managingVariantsProduct) {
      toast({
        title: 'Error',
        description: 'Product tidak ditemukan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingVariant(null);
    setVariantFormData({
      BPPRODUCTVARIANT_KEY: '',
      BPPRODUCTVARIANT_NAME: '',
      BPPRODUCTVARIANT_BARCODE: '',
      BPPRODUCTVARIANT_SKU: '',
      BPPRODUCTVARIANT_BASE64: '',
      BPPRODUCT_KEY: managingVariantsProduct.BPPRODUCT_KEY || '',
    });
    onVariantFormOpen();
  };

  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
    setVariantFormData({
      BPPRODUCTVARIANT_KEY: variant.BPPRODUCTVARIANT_KEY || '',
      BPPRODUCTVARIANT_NAME: variant.BPPRODUCTVARIANT_NAME || '',
      BPPRODUCTVARIANT_BARCODE: variant.BPPRODUCTVARIANT_BARCODE || '',
      BPPRODUCTVARIANT_SKU: variant.BPPRODUCTVARIANT_SKU || '',
      BPPRODUCTVARIANT_BASE64: variant.BPPRODUCTVARIANT_BASE64 || '',
      BPPRODUCT_KEY: variant.BPPRODUCT_KEY || managingVariantsProduct?.BPPRODUCT_KEY || '',
    });
    onVariantFormOpen();
  };

  const handleVariantInputChange = (e) => {
    const { name, value } = e.target;
    setVariantFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVariantImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Error',
        description: 'File harus berupa gambar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'Ukuran file maksimal 5MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
      setVariantFormData(prev => ({
        ...prev,
        BPPRODUCTVARIANT_BASE64: base64String
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleVariantSubmit = async () => {
    if (!variantFormData.BPPRODUCTVARIANT_KEY) {
      toast({
        title: 'Validasi gagal',
        description: 'Variant Key harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!variantFormData.BPPRODUCT_KEY) {
      toast({
        title: 'Validasi gagal',
        description: 'Product Key harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (editingVariant) {
        const response = await bpProductRetailApi.post(`/variant/update/${editingVariant.BPPRODUCTVARIANT_KEY}`, {
          BPPRODUCTVARIANT_NAME: variantFormData.BPPRODUCTVARIANT_NAME,
          BPPRODUCTVARIANT_BARCODE: variantFormData.BPPRODUCTVARIANT_BARCODE,
          BPPRODUCTVARIANT_SKU: variantFormData.BPPRODUCTVARIANT_SKU,
          BPPRODUCTVARIANT_BASE64: variantFormData.BPPRODUCTVARIANT_BASE64,
        });
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Variant berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onVariantFormClose();
          await fetchProductVariants(managingVariantsProduct.BPPRODUCT_ID);
        }
      } else {
        const response = await bpProductRetailApi.post('/variant', {
          ...variantFormData,
          CREATED_BY: user?.username || 'system'
        });
        if (response.data.status === 201 || response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Variant berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onVariantFormClose();
          await fetchProductVariants(managingVariantsProduct.BPPRODUCT_ID);
        }
      }
    } catch (error) {
      console.error('Error saving variant:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteVariantClick = (variant) => {
    setDeleteVariant(variant);
    onDeleteVariantOpen();
  };

  const handleDeleteVariantConfirm = async () => {
    if (!deleteVariant) return;

    try {
      const response = await bpProductRetailApi.post(`/variant/delete/${deleteVariant.BPPRODUCTVARIANT_KEY}`, {
        DELETED_BY: user?.username || 'system'
      });
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Variant berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteVariantClose();
        setDeleteVariant(null);
        await fetchProductVariants(managingVariantsProduct.BPPRODUCT_ID);
      }
    } catch (error) {
      console.error('Error deleting variant:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ========== ATTRIBUTE HANDLERS ==========

  const handleManageAttributes = async (variant) => {
    setManagingAttributesVariant(variant);
    onAttributesOpen();
    await fetchVariantAttributes(variant.BPPRODUCTVARIANT_KEY);
  };

  const fetchVariantAttributes = async (variantKey) => {
    setLoadingAttributes(true);
    try {
      const response = await bpProductRetailApi.get(`/variant/${variantKey}/attributes`);
      if (response.data.status === 200) {
        setVariantAttributes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching variant attributes:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data attributes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingAttributes(false);
    }
  };

  const handleCreateAttribute = () => {
    if (!managingAttributesVariant) {
      toast({
        title: 'Error',
        description: 'Variant tidak ditemukan',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingAttribute(null);
    setAttributeFormData({
      BPPRODUCTATTR_ATTRIBUTE: '',
      BPPRODUCTATTR_VALUE: '',
      BPPRODUCTATTR_DISPLAYNAME: '',
      BPPRODUCTVARIANT_KEY: managingAttributesVariant.BPPRODUCTVARIANT_KEY || '',
    });
    onAttributeFormOpen();
  };

  const handleEditAttribute = (attribute) => {
    setEditingAttribute(attribute);
    setAttributeFormData({
      BPPRODUCTATTR_ATTRIBUTE: attribute.BPPRODUCTATTR_ATTRIBUTE || '',
      BPPRODUCTATTR_VALUE: attribute.BPPRODUCTATTR_VALUE || '',
      BPPRODUCTATTR_DISPLAYNAME: attribute.BPPRODUCTATTR_DISPLAYNAME || '',
      BPPRODUCTVARIANT_KEY: managingAttributesVariant?.BPPRODUCTVARIANT_KEY || '',
    });
    onAttributeFormOpen();
  };

  const handleAttributeInputChange = (e) => {
    const { name, value } = e.target;
    setAttributeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAttributeSubmit = async () => {
    if (!attributeFormData.BPPRODUCTVARIANT_KEY) {
      toast({
        title: 'Validasi gagal',
        description: 'Variant Key harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (editingAttribute) {
        const response = await bpProductRetailApi.post(`/attribute/update/${editingAttribute.BPPRODUCTATTR_ID}`, {
          BPPRODUCTATTR_ATTRIBUTE: attributeFormData.BPPRODUCTATTR_ATTRIBUTE,
          BPPRODUCTATTR_VALUE: attributeFormData.BPPRODUCTATTR_VALUE,
          BPPRODUCTATTR_DISPLAYNAME: attributeFormData.BPPRODUCTATTR_DISPLAYNAME,
          BPPRODUCTVARIANT_KEY: attributeFormData.BPPRODUCTVARIANT_KEY,
        });
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Attribute berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onAttributeFormClose();
          await fetchVariantAttributes(attributeFormData.BPPRODUCTVARIANT_KEY);
        }
      } else {
        const response = await bpProductRetailApi.post('/attribute', {
          ...attributeFormData,
          CREATED_BY: user?.username || 'system'
        });
        if (response.data.status === 201 || response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'Attribute berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onAttributeFormClose();
          await fetchVariantAttributes(attributeFormData.BPPRODUCTVARIANT_KEY);
        }
      }
    } catch (error) {
      console.error('Error saving attribute:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteAttributeClick = (attribute) => {
    setDeleteAttribute(attribute);
    onDeleteAttributeOpen();
  };

  const handleDeleteAttributeConfirm = async () => {
    if (!deleteAttribute) return;

    try {
      const response = await bpProductRetailApi.post(`/attribute/delete/${deleteAttribute.BPPRODUCTATTR_ID}`, {
        DELETED_BY: user?.username || 'system'
      });
      if (response.data.status === 200) {
        toast({
          title: 'Berhasil',
          description: 'Attribute berhasil dihapus',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onDeleteAttributeClose();
        setDeleteAttribute(null);
        await fetchVariantAttributes(managingAttributesVariant.BPPRODUCTVARIANT_KEY);
      }
    } catch (error) {
      console.error('Error deleting attribute:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Sync data from external API
  const handleSyncData = async () => {
    if (isRetailProduct) {
      // For retail, directly call sync API (URL and key are hardcoded in backend)
      setSyncing(true);
      try {
        const response = await bpProductRetailApi.post('/sync', {});

        if (response.data.status === 200) {
          const { success, errors, skipped } = response.data.data || {};
          toast({
            title: 'Sync Berhasil',
            description: `Berhasil sync ${success || 0} produk${errors > 0 ? `, ${errors} error` : ''}${skipped > 0 ? `, ${skipped} dilewati` : ''}`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          // Refresh product list after sync
          await fetchProducts();
        }
      } catch (error) {
        console.error('Error syncing data:', error);
        toast({
          title: 'Error Sync',
          description: error.response?.data?.message || 'Terjadi kesalahan saat sync data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setSyncing(false);
      }
      return;
    }

    setSyncing(true);
    try {
      const response = await bpProductApi.post('/sync', {
        CREATED_BY: user?.username || user?.nip || 'SYNC_USER'
      });

      if (response.data.status === 200) {
        const { success, errors, skipped } = response.data.data || {};
        toast({
          title: 'Sync Berhasil',
          description: `Berhasil sync ${success || 0} produk${errors > 0 ? `, ${errors} error` : ''}${skipped > 0 ? `, ${skipped} dilewati` : ''}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Refresh product list after sync
        await fetchProducts();
      }
    } catch (error) {
      console.error('Error syncing data:', error);
      toast({
        title: 'Error Sync',
        description: error.response?.data?.message || 'Terjadi kesalahan saat sync data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncing(false);
    }
  };

  // Helper function to calculate status based on dates
  const getProductStatus = (product) => {
    if (isRetailProduct) {
      // For retail products, use BPPRODUCT_STATUS
      return {
        isActive: product.BPPRODUCT_STATUS === '1' || product.BPPRODUCT_STATUS === 1,
        label: product.BPPRODUCT_STATUS === '1' || product.BPPRODUCT_STATUS === 1 ? 'Active' : 'Not Active'
      };
    } else {
      // For non-retail products, calculate based on dates
      if (!product.BPPRODUCT_STARTDATE || !product.BPPRODUCT_ENDDATE) {
        return {
          isActive: false,
          label: 'Not Active'
        };
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(product.BPPRODUCT_STARTDATE);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(product.BPPRODUCT_ENDDATE);
      endDate.setHours(0, 0, 0, 0);

      if (today < startDate) {
        return {
          isActive: false,
          label: 'Belum Aktif'
        };
      } else if (today >= startDate && today <= endDate) {
        return {
          isActive: true,
          label: 'Active'
        };
      } else {
        return {
          isActive: false,
          label: 'Not Active'
        };
      }
    }
  };

  // DataTable columns
  const columns = useMemo(
    () => [
      {
        name: 'ID',
        selector: (row) => row.BPPRODUCT_ID,
        sortable: true,
        width: '80px',
        omit: isMobileView,
      },
      {
        name: 'Nama Product',
        selector: (row) => row.BPPRODUCT_NAME || '-',
        sortable: true,
        cell: (row) => (
          <VStack align="start" spacing={1}>
            <Text fontWeight="600" fontSize={{ base: 'sm', lg: 'md' }}>
              {row.BPPRODUCT_NAME || '-'}
            </Text>
            {isMobileView && (
              <Text fontSize="xs" color="gray.500">
                ID: {row.BPPRODUCT_ID} | {isRetailProduct ? `Key: ${row.BPPRODUCT_KEY || '-'}` : `Code: ${row.BPPRODUCT_CODE || '-'}`}
              </Text>
            )}
          </VStack>
        ),
      },
      {
        name: isRetailProduct ? 'Product Key' : 'Product Code',
        selector: (row) => isRetailProduct ? (row.BPPRODUCT_KEY || '-') : (row.BPPRODUCT_CODE || '-'),
        sortable: true,
        omit: isMobileView,
      },
      ...(isRetailProduct ? [{
        name: 'Category',
        selector: (row) => row.BPPRODUCT_CATEGORY || '-',
        sortable: true,
        omit: isMobileView,
      }] : [{
        name: 'Category',
        selector: (row) => row.BPPRODUCT_CATEGORY || '-',
        sortable: true,
        omit: isMobileView,
      }]),
      {
        name: 'Status',
        selector: (row) => {
          const status = getProductStatus(row);
          return status.isActive ? 1 : 0;
        },
        sortable: true,
        cell: (row) => {
          const status = getProductStatus(row);
          return (
            <HStack spacing={1}>
              <Badge
                colorScheme={status.isActive ? 'green' : 'red'}
                fontSize={{ base: 'xs', lg: 'sm' }}
              >
                {status.label}
              </Badge>
              {row.BPPRODUCT_APISTATUS === 'published' && (
                <Badge colorScheme="green" variant="solid" fontSize="xs" title="Published to API Hub">
                  API
                </Badge>
              )}
            </HStack>
          );
        },
      },
      {
        name: 'Aksi',
        cell: (row) => (
          <HStack spacing={1} justify="center">
            <IconButton
              icon={<FiEye />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="green"
              variant="ghost"
              onClick={() => handleView(row)}
              aria-label="View"
            />
            <IconButton
              icon={<FiEdit />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="blue"
              variant="ghost"
              onClick={() => handleEdit(row)}
              aria-label="Edit"
            />
            <IconButton
              icon={<FiImage />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="purple"
              variant="ghost"
              onClick={() => handleManageImages(row)}
              aria-label="Manage Images"
            />
            {isRetailProduct && (
              <IconButton
                icon={<FiPackage />}
                size={{ base: 'xs', lg: 'sm' }}
                colorScheme="orange"
                variant="ghost"
                onClick={() => handleManageStocks(row)}
                aria-label="Manage Stocks"
              />
            )}
            {isRetailProduct && (
              <IconButton
                icon={<FiPlus />}
                size={{ base: 'xs', lg: 'sm' }}
                colorScheme="teal"
                variant="ghost"
                onClick={() => handleManageVariants(row)}
                aria-label="Manage Variants"
                title="Manage Variants"
              />
            )}
            <IconButton
              icon={<FiSend />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme={row.BPPRODUCT_APISTATUS === 'published' ? 'orange' : 'emerald'}
              variant="ghost"
              onClick={() => handlePublishToggle(row)}
              aria-label={row.BPPRODUCT_APISTATUS === 'published' ? 'Unpublish API' : 'Publish API'}
              title={row.BPPRODUCT_APISTATUS === 'published' ? 'Unpublish from API Hub' : 'Publish to API Hub'}
            />
            <IconButton
              icon={<FiTrash2 />}
              size={{ base: 'xs', lg: 'sm' }}
              colorScheme="red"
              variant="ghost"
              onClick={() => handleDeleteClick(row)}
              aria-label="Delete"
            />
          </HStack>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '220px',
      },
    ],
    [isMobileView]
  );

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f7fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: 'Baris per halaman:',
    rangeSeparatorText: 'dari',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Semua',
  };

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const searchLower = searchTerm.toLowerCase();
    return products.filter((product) => {
      return (
        (product.BPPRODUCT_NAME && product.BPPRODUCT_NAME.toLowerCase().includes(searchLower)) ||
        (isRetailProduct
          ? (product.BPPRODUCT_KEY && product.BPPRODUCT_KEY.toLowerCase().includes(searchLower)) ||
          (product.BPPRODUCT_CATEGORY && product.BPPRODUCT_CATEGORY.toLowerCase().includes(searchLower))
          : (product.BPPRODUCT_CODE && product.BPPRODUCT_CODE.toLowerCase().includes(searchLower)) ||
          (product.MEMBER_CODE && product.MEMBER_CODE.toLowerCase().includes(searchLower)) ||
          (product.BPPRODUCT_CATEGORY && product.BPPRODUCT_CATEGORY.toLowerCase().includes(searchLower))
        ) ||
        (product.BPPRODUCT_ID && product.BPPRODUCT_ID.toString().includes(searchLower))
      );
    });
  }, [products, searchTerm, isRetailProduct]);

  return (
    <>
      <VStack align="stretch" spacing={4} mb={6}>
        <HStack spacing={3} flexWrap="wrap">
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchProducts}
            isLoading={loading}
            variant="outline"
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Refresh
          </Button>
          {/* Sync Data button only for Injourney Retail (memberId === 'srn') */}
          {memberId === 'srn' && (
            <Button
              leftIcon={<FiDownload />}
              onClick={handleSyncData}
              isLoading={syncing}
              variant="outline"
              colorScheme="green"
              size={{ base: 'sm', lg: 'md' }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Sync Data</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Sync</Text>
            </Button>
          )}
          <Button
            leftIcon={<FiPlus />}
            onClick={handleCreate}
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Tambah Product</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Tambah</Text>
          </Button>
        </HStack>
      </VStack>

      <DataTable
        columns={columns}
        data={filteredProducts}
        progressPending={loading}
        progressComponent={<Text py={8}>Loading...</Text>}
        noDataComponent={
          <Text py={8} color="gray.500" textAlign="center">
            Tidak ada data product
          </Text>
        }
        subHeader
        subHeaderComponent={
          <Input
            placeholder="Cari product (nama, SKU, ID, member)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            mb={4}
            maxW="400px"
          />
        }
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
        paginationComponentOptions={paginationComponentOptions}
        highlightOnHover
        pointerOnHover
        customStyles={customStyles}
        responsive
        dense
        striped
      />

      {/* Published Packages Section */}
      {!isRetailProduct && (
        <Box mt={8}>
          <Button
            onClick={() => setShowPackages(!showPackages)}
            variant="outline"
            colorScheme="brand"
            size="sm"
            leftIcon={showPackages ? <FiChevronUp /> : <FiChevronDown />}
            mb={3}
          >
            Published Packages ({packages.filter(p => p.PACKAGE_APISTATUS === 'published').length})
          </Button>
          {showPackages && (
            <DataTable
              columns={[
                {
                  name: 'Package Name',
                  selector: row => row.PACKAGE_NAME || '-',
                  sortable: true,
                  cell: row => (
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="600" fontSize="sm">{row.PACKAGE_NAME || '-'}</Text>
                      <Text fontSize="xs" color="gray.500">Code: {row.PACKAGE_CODE}</Text>
                    </VStack>
                  ),
                },
                {
                  name: 'Code',
                  selector: row => row.PACKAGE_CODE || '-',
                  sortable: true,
                  omit: isMobileView,
                },
                {
                  name: 'Price',
                  selector: row => row.PACKAGE_DISCOUNTPRICE || row.PACKAGE_BASEPRICE || 0,
                  sortable: true,
                  cell: row => (
                    <Text fontSize="sm">
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(row.PACKAGE_DISCOUNTPRICE || row.PACKAGE_BASEPRICE || 0)}
                    </Text>
                  ),
                },
                {
                  name: 'Quota',
                  selector: row => row.PACKAGE_KUOTA || 0,
                  sortable: true,
                  omit: isMobileView,
                },
                {
                  name: 'Status',
                  cell: row => (
                    <Badge colorScheme={row.PACKAGE_APISTATUS === 'published' ? 'green' : 'gray'}>
                      {row.PACKAGE_APISTATUS === 'published' ? 'Published' : row.PACKAGE_APISTATUS || 'Draft'}
                    </Badge>
                  ),
                },
              ]}
              data={packages.filter(p => p.PACKAGE_APISTATUS === 'published')}
              progressPending={loadingPackages}
              progressComponent={<Text py={8}>Loading packages...</Text>}
              noDataComponent={<Text py={8} color="gray.500" textAlign="center">No published packages for this member</Text>}
              pagination
              paginationPerPage={5}
              highlightOnHover
              pointerOnHover
              customStyles={customStyles}
              responsive
              dense
              striped
            />
          )}
        </Box>
      )}

      {/* Create/Edit Product Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingProduct ? 'Edit Product' : importedSourceProduct ? 'Import Produk Source ke PRIME' : 'Tambah Product Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {importedSourceProduct && (
                <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="md" p={3}>
                  <HStack spacing={2} mb={1}>
                    <Badge colorScheme="blue">{importedSourceProduct?.sourceLabel || 'IDM API'}</Badge>
                    <Text fontSize="sm" fontWeight="600" color="blue.800">
                      Data dasar produk sudah diisi dari source API.
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="blue.700">
                    Review nama, harga, kategori, gambar, periode, dan metadata lain sebelum menyimpan ke PRIME Inventory.
                  </Text>
                </Box>
              )}
              <FormControl isRequired>
                <FormLabel>Nama Product</FormLabel>
                <Input
                  name="BPPRODUCT_NAME"
                  value={formData.BPPRODUCT_NAME}
                  onChange={handleInputChange}
                  placeholder="Masukkan nama product"
                />
              </FormControl>

              {isRetailProduct ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Product Key (Product ID)</FormLabel>
                    <Input
                      name="BPPRODUCT_KEY"
                      value={formData.BPPRODUCT_KEY}
                      onChange={handleInputChange}
                      placeholder="Masukkan Product Key (Product ID)"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Category</FormLabel>
                    <Select
                      name="BPPRODUCT_CATEGORY"
                      value={formData.BPPRODUCT_CATEGORY}
                      onChange={handleInputChange}
                      placeholder="Pilih Category"
                    >
                      <option value="Ammenities">Ammenities</option>
                      <option value="Wellness">Wellness</option>
                      <option value="Transportation">Transportation</option>
                      <option value="Airport Lounges">Airport Lounges</option>
                      <option value="Baggage Wrapping">Baggage Wrapping</option>
                      <option value="Concierge / Passanger Services">Concierge / Passanger Services</option>
                      <option value="Attraction">Attraction</option>
                      <option value="TMII Attraction">TMII Attraction</option>
                      <option value="Integration Test">Integration Test</option>
                      <option value="Sarinah Coupon">Sarinah Coupon</option>
                      {/* Harus sama persis dengan kunci di supplierRegistry.config.js —
                          string inilah yang mengarahkan pembelian ke adapter goers-pool. */}
                      <option value="GOERS Event">GOERS Event</option>
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>Product Code</FormLabel>
                    <Input
                      name="BPPRODUCT_CODE"
                      value={formData.BPPRODUCT_CODE}
                      onChange={handleInputChange}
                      placeholder="Product Code akan ter-generate otomatis"
                      isReadOnly
                      bg="gray.50"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      name="BPPRODUCT_DESC"
                      value={formData.BPPRODUCT_DESC}
                      onChange={handleInputChange}
                      placeholder="Masukkan Description"
                      rows={3}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Publish Price</FormLabel>
                    <Input
                      name="BPPRODUCT_PUBLISHPRICE"
                      value={formData.BPPRODUCT_PUBLISHPRICE}
                      onChange={handleInputChange}
                      placeholder="Masukkan Publish Price (contoh: 100.000)"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Corp Price</FormLabel>
                    <Input
                      name="BPPRODUCT_CORPPRICE"
                      value={formData.BPPRODUCT_CORPPRICE}
                      onChange={handleInputChange}
                      placeholder="Masukkan Corp Price (contoh: 100.000)"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Ecosystem Price</FormLabel>
                    <Input
                      name="BPPRODUCT_AGENTPRICE"
                      value={formData.BPPRODUCT_AGENTPRICE}
                      onChange={handleInputChange}
                      placeholder="Masukkan Ecosystem Price (contoh: 100.000)"
                    />
                  </FormControl>

                  {/* Discount Information in Form */}
                  {formData.BPPRODUCT_PUBLISHPRICE && parseRupiah(formData.BPPRODUCT_PUBLISHPRICE) && parseFloat(parseRupiah(formData.BPPRODUCT_PUBLISHPRICE)) > 0 && (
                    <Box mt={2} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                      <Text fontSize="xs" fontWeight="600" color="blue.700" mb={2}>Informasi Discount</Text>
                      <VStack align="stretch" spacing={2}>
                        {formData.BPPRODUCT_CORPPRICE && parseRupiah(formData.BPPRODUCT_CORPPRICE) && parseFloat(parseRupiah(formData.BPPRODUCT_CORPPRICE)) > 0 && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">Discount Price (Corporate):</Text>
                            <Text fontSize="sm" fontWeight="600" color="blue.700">
                              {(((parseFloat(parseRupiah(formData.BPPRODUCT_PUBLISHPRICE)) - parseFloat(parseRupiah(formData.BPPRODUCT_CORPPRICE))) / parseFloat(parseRupiah(formData.BPPRODUCT_PUBLISHPRICE))) * 100).toFixed(2)}%
                            </Text>
                          </HStack>
                        )}
                        {formData.BPPRODUCT_AGENTPRICE && parseRupiah(formData.BPPRODUCT_AGENTPRICE) && parseFloat(parseRupiah(formData.BPPRODUCT_AGENTPRICE)) > 0 && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">Discount Ecosystem:</Text>
                            <Text fontSize="sm" fontWeight="600" color="blue.700">
                              {(((parseFloat(parseRupiah(formData.BPPRODUCT_PUBLISHPRICE)) - parseFloat(parseRupiah(formData.BPPRODUCT_AGENTPRICE))) / parseFloat(parseRupiah(formData.BPPRODUCT_PUBLISHPRICE))) * 100).toFixed(2)}%
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  )}

                  <HStack spacing={4}>
                    <FormControl flex={1}>
                      <FormLabel>Quantity</FormLabel>
                      <Input
                        name="BPPRODUCT_QUANTITY"
                        value={formData.BPPRODUCT_QUANTITY}
                        onChange={handleInputChange}
                        placeholder="Masukkan Quantity"
                        type="number"
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel>Unit</FormLabel>
                      <Input
                        name="BPPRODUCT_UNIT"
                        value={formData.BPPRODUCT_UNIT}
                        onChange={handleInputChange}
                        placeholder="Masukkan Unit"
                      />
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel>Category</FormLabel>
                    <Select
                      name="BPPRODUCT_CATEGORY"
                      value={formData.BPPRODUCT_CATEGORY}
                      onChange={handleInputChange}
                      placeholder="Pilih Category"
                    >
                      <option value="Ammenities">Ammenities</option>
                      <option value="Wellness">Wellness</option>
                      <option value="Transportation">Transportation</option>
                      <option value="Airport Lounges">Airport Lounges</option>
                      <option value="Airport Lounge">Airport Lounge</option>
                      <option value="Baggage Wrapping">Baggage Wrapping</option>
                      <option value="Concierge / Passanger Services">Concierge / Passanger Services</option>
                      <option value="Attraction">Attraction</option>
                      <option value="TMII Attraction">TMII Attraction</option>
                      <option value="Integration Test">Integration Test</option>
                      <option value="Sarinah Coupon">Sarinah Coupon</option>
                      {/* Harus sama persis dengan kunci di supplierRegistry.config.js —
                          string inilah yang mengarahkan pembelian ke adapter goers-pool. */}
                      <option value="GOERS Event">GOERS Event</option>
                    </Select>
                  </FormControl>
                  {isJoumpaCategory(formData.BPPRODUCT_CATEGORY) && (
                    <FormControl isRequired>
                      <FormLabel>Tipe Joumpa (Arrival / Departure / Transfer)</FormLabel>
                      <RadioGroup
                        value={formData.FLT_TYPE || ''}
                        onChange={(value) => setFormData((prev) => ({ ...prev, FLT_TYPE: value }))}
                      >
                        <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
                          <Radio value="arrival">Arrival</Radio>
                          <Radio value="departure">Departure</Radio>
                          <Radio value="transfer">Transfer</Radio>
                        </Stack>
                      </RadioGroup>
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        Wajib untuk Concierge / Joumpa. Tipe ini dikirim ke GOERS sebagai route.fltType; jadwal penerbangan tetap diisi di GOERS.
                      </Text>
                    </FormControl>
                  )}
                  <FormControl>
                    <FormLabel>Asset Service Owner</FormLabel>
                    <Input
                      name="BPPRODUCT_ASSETSERVICEOWNER"
                      value={formData.BPPRODUCT_ASSETSERVICEOWNER}
                      onChange={handleInputChange}
                      placeholder="Masukkan Asset Service Owner"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Kategori Pemesanan</FormLabel>
                    <Select
                      name="BOOKING_CATEGORY"
                      value={formData.BOOKING_CATEGORY || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Default (Payment)</option>
                      <option value="payment">Payment - langsung bayar</option>
                      <option value="reservation">Reservation - konfirmasi ketersediaan dulu</option>
                    </Select>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Reservation dipakai untuk produk HOTEL: GOERS baru boleh menagih setelah admin mengonfirmasi kamar dan memasukkan reservation code.
                    </Text>
                  </FormControl>
                  {formData.BOOKING_CATEGORY === 'reservation' && (
                    <FormControl>
                      <FormLabel>Hotel</FormLabel>
                      <Select
                        name="HOTEL_CODE"
                        value={formData.HOTEL_CODE || ''}
                        onChange={handleInputChange}
                        placeholder="Pilih hotel"
                      >
                        {hinHotels.map((h) => (
                          <option key={h.hotel_id || h.hotel_code} value={h.hotel_code}>
                            {h.hotel_code} - {h.hotel_name}{h.hotel_email ? '' : ' (email belum diisi)'}
                          </option>
                        ))}
                      </Select>
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        Permintaan konfirmasi kamar dikirim ke email hotel ini di Hotel Master.
                      </Text>
                    </FormControl>
                  )}
                  {formData.BOOKING_CATEGORY === 'reservation' && (
                    <>
                      <FormControl isRequired>
                        <FormLabel>Tipe kamar</FormLabel>
                        <Input name="ROOM_TYPE" value={formData.ROOM_TYPE || ''} onChange={handleInputChange} placeholder="Kode atau nama tipe kamar" />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel>Jumlah kamar per reservasi</FormLabel>
                        <Input name="ROOM_QTY" type="number" min={1} step={1} value={formData.ROOM_QTY || ''} onChange={handleInputChange} />
                        <Text fontSize="sm" color="gray.500">Jumlah kamar yang diminta dalam satu reservasi.</Text>
                      </FormControl>
                    </>
                  )}
                  <FormControl>
                    <FormLabel>Lokasi</FormLabel>
                    <Input
                      name="LOCATION"
                      value={formData.LOCATION}
                      onChange={handleInputChange}
                      placeholder="Contoh: Jakarta, CGK, Magelang"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Price Includes</FormLabel>
                    <Textarea
                      name="PRICE_INCLUDES"
                      value={formData.PRICE_INCLUDES}
                      onChange={handleInputChange}
                      placeholder="Contoh: Tiket masuk, welcome drink"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Price Excludes</FormLabel>
                    <Textarea
                      name="PRICE_EXCLUDES"
                      value={formData.PRICE_EXCLUDES}
                      onChange={handleInputChange}
                      placeholder="Contoh: Transportasi, oleh-oleh"
                    />
                  </FormControl>
                  <HStack spacing={4}>
                    <FormControl flex={1}>
                      <FormLabel>Start Date</FormLabel>
                      <Input
                        name="BPPRODUCT_STARTDATE"
                        type="date"
                        value={formData.BPPRODUCT_STARTDATE}
                        onChange={handleInputChange}
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel>End Date</FormLabel>
                      <Input
                        name="BPPRODUCT_ENDDATE"
                        type="date"
                        value={formData.BPPRODUCT_ENDDATE}
                        onChange={handleInputChange}
                        min={formData.BPPRODUCT_STARTDATE || ''}
                      />
                    </FormControl>
                  </HStack>
                </>
              )}

              {memberId && !isRetailProduct && (
                <FormControl>
                  <FormLabel>Member Code</FormLabel>
                  <Input
                    name="MEMBER_CODE"
                    value={MEMBER_CODE_MAPPING[memberId.toLowerCase()] || memberId}
                    onChange={handleInputChange}
                    placeholder="Member Code"
                    isReadOnly
                    bg="gray.50"
                    title={`Kode: ${memberId}`}
                  />
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Batal
            </Button>
            <Button colorScheme="brand" onClick={handleSubmit}>
              {editingProduct ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Product Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Detail Product</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingProduct && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Nama Product</Text>
                  <Text fontSize="md" fontWeight="600" color="gray.800">{viewingProduct.BPPRODUCT_NAME || '-'}</Text>
                </Box>

                <HStack spacing={4}>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
                      {isRetailProduct ? 'Product Key' : 'Product Code'}
                    </Text>
                    <Text fontSize="md" color="gray.700">
                      {isRetailProduct ? (viewingProduct.BPPRODUCT_KEY || '-') : (viewingProduct.BPPRODUCT_CODE || '-')}
                    </Text>
                  </Box>
                  {!isRetailProduct && (
                    <Box flex={1}>
                      <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Member Code</Text>
                      <Text fontSize="md" color="gray.700">
                        {viewingProduct.MEMBER_CODE ? (MEMBER_CODE_MAPPING[viewingProduct.MEMBER_CODE.toLowerCase()] || viewingProduct.MEMBER_CODE) : '-'}
                      </Text>
                    </Box>
                  )}
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Category</Text>
                    <Text fontSize="md" color="gray.700">{viewingProduct.BPPRODUCT_CATEGORY || '-'}</Text>
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Status</Text>
                    {isRetailProduct ? (
                      <Badge colorScheme={viewingProduct.BPPRODUCT_STATUS === '1' || viewingProduct.BPPRODUCT_STATUS === 1 ? 'green' : 'red'}>
                        {viewingProduct.BPPRODUCT_STATUS === '1' || viewingProduct.BPPRODUCT_STATUS === 1 ? 'Active' : 'Not Active'}
                      </Badge>
                    ) : (
                      (() => {
                        const status = getProductStatus(viewingProduct);
                        return (
                          <Badge colorScheme={status.isActive ? 'green' : 'red'}>
                            {status.label}
                          </Badge>
                        );
                      })()
                    )}
                  </Box>
                </HStack>

                {!isRetailProduct && (
                  <>
                    {viewingProduct.BPPRODUCT_DESC && (
                      <Box>
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Description</Text>
                        <Text fontSize="md" color="gray.700">{viewingProduct.BPPRODUCT_DESC}</Text>
                      </Box>
                    )}
                    <HStack spacing={4}>
                      {viewingProduct.BPPRODUCT_PUBLISHPRICE && (
                        <Box flex={1}>
                          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Publish Price</Text>
                          <Text fontSize="md" color="gray.700">
                            {new Intl.NumberFormat('id-ID', {
                              style: 'currency',
                              currency: 'IDR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(viewingProduct.BPPRODUCT_PUBLISHPRICE)}
                          </Text>
                        </Box>
                      )}
                      {viewingProduct.BPPRODUCT_CORPPRICE && (
                        <Box flex={1}>
                          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Corp Price</Text>
                          <Text fontSize="md" color="gray.700">
                            {new Intl.NumberFormat('id-ID', {
                              style: 'currency',
                              currency: 'IDR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(viewingProduct.BPPRODUCT_CORPPRICE)}
                          </Text>
                        </Box>
                      )}
                      {viewingProduct.BPPRODUCT_AGENTPRICE && (
                        <Box flex={1}>
                          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Ecosystem Price</Text>
                          <Text fontSize="md" color="gray.700">
                            {new Intl.NumberFormat('id-ID', {
                              style: 'currency',
                              currency: 'IDR',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(viewingProduct.BPPRODUCT_AGENTPRICE)}
                          </Text>
                        </Box>
                      )}
                    </HStack>

                    {/* Discount Information */}
                    {!isRetailProduct && viewingProduct.BPPRODUCT_PUBLISHPRICE && parseFloat(viewingProduct.BPPRODUCT_PUBLISHPRICE) > 0 && (viewingProduct.BPPRODUCT_CORPPRICE || viewingProduct.BPPRODUCT_AGENTPRICE) && (
                      <Box mt={4} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                        <Text fontSize="xs" fontWeight="600" color="blue.700" mb={2}>Informasi Discount</Text>
                        <VStack align="stretch" spacing={2}>
                          {viewingProduct.BPPRODUCT_CORPPRICE && parseFloat(viewingProduct.BPPRODUCT_CORPPRICE) > 0 && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">Discount Price (Corporate):</Text>
                              <Text fontSize="sm" fontWeight="600" color="blue.700">
                                {(((parseFloat(viewingProduct.BPPRODUCT_PUBLISHPRICE) - parseFloat(viewingProduct.BPPRODUCT_CORPPRICE)) / parseFloat(viewingProduct.BPPRODUCT_PUBLISHPRICE)) * 100).toFixed(2)}%
                              </Text>
                            </HStack>
                          )}
                          {viewingProduct.BPPRODUCT_AGENTPRICE && parseFloat(viewingProduct.BPPRODUCT_AGENTPRICE) > 0 && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">Discount Ecosystem:</Text>
                              <Text fontSize="sm" fontWeight="600" color="blue.700">
                                {(((parseFloat(viewingProduct.BPPRODUCT_PUBLISHPRICE) - parseFloat(viewingProduct.BPPRODUCT_AGENTPRICE)) / parseFloat(viewingProduct.BPPRODUCT_PUBLISHPRICE)) * 100).toFixed(2)}%
                              </Text>
                            </HStack>
                          )}
                        </VStack>
                      </Box>
                    )}

                    {!isRetailProduct && (viewingProduct.BPPRODUCT_QUANTITY || viewingProduct.BPPRODUCT_UNIT) && (
                      <HStack spacing={4} mt={4} align="flex-start">
                        {viewingProduct.BPPRODUCT_QUANTITY && (
                          <Box flex={1}>
                            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Quantity</Text>
                            <Text fontSize="md" color="gray.700">{viewingProduct.BPPRODUCT_QUANTITY}</Text>
                          </Box>
                        )}
                        {viewingProduct.BPPRODUCT_UNIT && (
                          <Box flex={1}>
                            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Unit</Text>
                            <Text fontSize="md" color="gray.700">{viewingProduct.BPPRODUCT_UNIT}</Text>
                          </Box>
                        )}
                      </HStack>
                    )}
                    {!isRetailProduct && viewingProduct.BPPRODUCT_ASSETSERVICEOWNER && (
                      <Box mt={4}>
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Asset Service Owner</Text>
                        <Text fontSize="md" color="gray.700">{viewingProduct.BPPRODUCT_ASSETSERVICEOWNER}</Text>
                      </Box>
                    )}
                    {!isRetailProduct && (viewingProduct.BPPRODUCT_STARTDATE || viewingProduct.BPPRODUCT_ENDDATE) && (
                      <HStack spacing={4} mt={4} align="flex-start">
                        {viewingProduct.BPPRODUCT_STARTDATE && (
                          <Box flex={1}>
                            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Start Date</Text>
                            <Text fontSize="md" color="gray.700">
                              {new Date(viewingProduct.BPPRODUCT_STARTDATE).toLocaleDateString('id-ID', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Text>
                          </Box>
                        )}
                        {viewingProduct.BPPRODUCT_ENDDATE && (
                          <Box flex={1}>
                            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>End Date</Text>
                            <Text fontSize="md" color="gray.700">
                              {new Date(viewingProduct.BPPRODUCT_ENDDATE).toLocaleDateString('id-ID', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Text>
                          </Box>
                        )}
                      </HStack>
                    )}
                    {!isRetailProduct && viewingProduct.BPPRODUCT_STARTDATE && viewingProduct.BPPRODUCT_ENDDATE && (
                      <Box mt={4} p={3} bg={(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const startDate = new Date(viewingProduct.BPPRODUCT_STARTDATE);
                        startDate.setHours(0, 0, 0, 0);
                        const endDate = new Date(viewingProduct.BPPRODUCT_ENDDATE);
                        endDate.setHours(0, 0, 0, 0);
                        if (today >= startDate && today <= endDate) {
                          return 'green.50';
                        } else {
                          return 'red.50';
                        }
                      })()} borderRadius="md" borderWidth="1px" borderColor={(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const startDate = new Date(viewingProduct.BPPRODUCT_STARTDATE);
                        startDate.setHours(0, 0, 0, 0);
                        const endDate = new Date(viewingProduct.BPPRODUCT_ENDDATE);
                        endDate.setHours(0, 0, 0, 0);
                        if (today >= startDate && today <= endDate) {
                          return 'green.200';
                        } else {
                          return 'red.200';
                        }
                      })()}>
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Status</Text>
                        <Text fontSize="md" fontWeight="600" color={(() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const startDate = new Date(viewingProduct.BPPRODUCT_STARTDATE);
                          startDate.setHours(0, 0, 0, 0);
                          const endDate = new Date(viewingProduct.BPPRODUCT_ENDDATE);
                          endDate.setHours(0, 0, 0, 0);
                          if (today >= startDate && today <= endDate) {
                            return 'green.700';
                          } else {
                            return 'red.700';
                          }
                        })()}>
                          {(() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const startDate = new Date(viewingProduct.BPPRODUCT_STARTDATE);
                            startDate.setHours(0, 0, 0, 0);
                            const endDate = new Date(viewingProduct.BPPRODUCT_ENDDATE);
                            endDate.setHours(0, 0, 0, 0);
                            if (today < startDate) {
                              return 'Belum Aktif';
                            } else if (today >= startDate && today <= endDate) {
                              return 'Aktif';
                            } else {
                              return 'Tidak Aktif';
                            }
                          })()}
                        </Text>
                      </Box>
                    )}
                  </>
                )}

                {/* Variants for Retail Products */}
                {isRetailProduct && viewingProduct.variants && viewingProduct.variants.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>Variants</Text>
                    <VStack align="stretch" spacing={3}>
                      {viewingProduct.variants.map((variant) => (
                        <Box key={variant.BPPRODUCTVARIANT_ID} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
                          <VStack align="stretch" spacing={2}>
                            <Text fontSize="sm" fontWeight="600">{variant.BPPRODUCTVARIANT_NAME || '-'}</Text>
                            <HStack spacing={4}>
                              <Text fontSize="xs" color="gray.500">SKU: {variant.BPPRODUCTVARIANT_SKU || '-'}</Text>
                              <Text fontSize="xs" color="gray.500">Barcode: {variant.BPPRODUCTVARIANT_BARCODE || '-'}</Text>
                            </HStack>
                            {variant.attributes && variant.attributes.length > 0 && (
                              <Box>
                                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Attributes:</Text>
                                <VStack align="stretch" spacing={1}>
                                  {variant.attributes.map((attr, idx) => (
                                    <Text key={idx} fontSize="xs" color="gray.600">
                                      {attr.BPPRODUCTATTR_DISPLAYNAME || `${attr.BPPRODUCTATTR_ATTRIBUTE}: ${attr.BPPRODUCTATTR_VALUE}`}
                                    </Text>
                                  ))}
                                </VStack>
                              </Box>
                            )}
                            {variant.rows && variant.rows.length > 0 && (
                              <Box>
                                <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Stocks:</Text>
                                <VStack align="stretch" spacing={1}>
                                  {variant.rows.map((row, idx) => (
                                    <Box key={idx} p={2} bg="gray.50" borderRadius="sm">
                                      <Text fontSize="xs" color="gray.700">
                                        {row.BPPRODUCTSTOCKS_LOCATIONNAME || row.locationName || '-'} |
                                        Stock: {row.BPPRODUCTSTOCKS_STOCK || row.stock || '0'} |
                                        Price: {row.BPPRODUCTSTOCKS_SELLPRICE || row.sellPrice || '-'}
                                      </Text>
                                    </Box>
                                  ))}
                                </VStack>
                              </Box>
                            )}
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {!isRetailProduct && viewingProduct.images && viewingProduct.images.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>Images</Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {viewingProduct.images.map((img) => (
                        <Image
                          key={img.BPPRODUCTIMAGE_ID}
                          src={
                            img.BPPRODUCTIMAGE_BASE64
                              ? `data:image/jpeg;base64,${img.BPPRODUCTIMAGE_BASE64}`
                              : img.BPPRODUCTIMAGE_FILE
                                ? `/images/products/${img.BPPRODUCTIMAGE_FILE}`
                                : null
                          }
                          alt={img.BPPRODUCTIMAGE_NAME}
                          maxH="100px"
                          maxW="100px"
                          objectFit="contain"
                          borderRadius="md"
                          fallback={
                            <Box
                              w="100px"
                              h="100px"
                              bg="gray.100"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              borderRadius="md"
                            >
                              <Text fontSize="xs" color="gray.500">
                                {img.BPPRODUCTIMAGE_BASE64 ? 'Memuat...' : 'Tidak ada'}
                              </Text>
                            </Box>
                          }
                        />
                      ))}
                    </HStack>
                  </Box>
                )}

                {!isRetailProduct && viewingProduct.stocks && viewingProduct.stocks.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>Stocks</Text>
                    <VStack align="stretch" spacing={2}>
                      {viewingProduct.stocks.map((stock) => (
                        <Box key={stock.BPPRODUCTSTOCKS_ID} p={2} border="1px solid" borderColor="gray.200" borderRadius="md">
                          <HStack justify="space-between">
                            <VStack align="start" spacing={1}>
                              <Text fontSize="sm" fontWeight="600">{stock.BPPRODUCTSTOCKS_LOCATIONNAME || '-'}</Text>
                              <Text fontSize="xs" color="gray.500">Stock: {stock.BPPRODUCTSTOCKS_STOCK || '0'}</Text>
                              <Text fontSize="xs" color="gray.500">Price: {stock.BPPRODUCTSTOCKS_SELLPRICE || '-'}</Text>
                            </VStack>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Tutup</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Product
            </AlertDialogHeader>
            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus product <strong>{deleteProduct?.BPPRODUCT_NAME}</strong>?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Manage Images Modal */}
      <Modal isOpen={isImagesOpen} onClose={onImagesClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Kelola Gambar - {managingImagesProduct?.BPPRODUCT_NAME}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {!isRetailProduct && (
                <Box>
                  <FormControl>
                    <FormLabel>Upload Gambar Baru (Multiple)</FormLabel>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Format: JPG, PNG, GIF. Maksimal 5MB per file.
                    </Text>
                  </FormControl>
                  {uploadingImage && (
                    <HStack mt={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.600">Mengupload gambar...</Text>
                    </HStack>
                  )}
                </Box>
              )}
              {!isRetailProduct && <Divider />}

              <Box>
                <Text fontSize="md" fontWeight="600" mb={3}>
                  Daftar Gambar ({productImages.length})
                </Text>
                {loadingImages ? (
                  <VStack py={8}>
                    <Spinner size="lg" />
                    <Text color="gray.500">Memuat gambar...</Text>
                  </VStack>
                ) : productImages.length === 0 ? (
                  <Text py={8} color="gray.500" textAlign="center">
                    Belum ada gambar untuk product ini
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {productImages.map((image) => (
                      <Box
                        key={image.BPPRODUCTIMAGE_ID}
                        p={4}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="8px"
                      >
                        <HStack justify="space-between" align="start">
                          <VStack align="start" spacing={2} flex={1}>
                            <Text fontSize="sm" fontWeight="500">
                              {image.BPPRODUCTIMAGE_NAME}
                            </Text>
                            {isRetailProduct && image.BPPRODUCTVARIANT_KEY && (
                              <Text fontSize="xs" color="gray.400">
                                Variant Key: {image.BPPRODUCTVARIANT_KEY}
                              </Text>
                            )}
                            <Image
                              src={
                                image.BPPRODUCTIMAGE_BASE64
                                  ? `data:image/jpeg;base64,${image.BPPRODUCTIMAGE_BASE64}`
                                  : image.BPPRODUCTIMAGE_FILE
                                    ? `/images/products/${image.BPPRODUCTIMAGE_FILE}`
                                    : null
                              }
                              alt={image.BPPRODUCTIMAGE_NAME || 'Product image'}
                              maxH="150px"
                              maxW="200px"
                              objectFit="contain"
                              fallback={
                                <Box
                                  w="200px"
                                  h="150px"
                                  bg="gray.100"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  borderRadius="4px"
                                >
                                  <Text fontSize="xs" color="gray.500">
                                    {image.BPPRODUCTIMAGE_BASE64 ? 'Memuat gambar...' : 'Gambar tidak ditemukan'}
                                  </Text>
                                </Box>
                              }
                            />
                          </VStack>
                          {!isRetailProduct && (
                            <IconButton
                              icon={<FiTrash2 />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDeleteImageClick(image)}
                              aria-label="Delete Image"
                            />
                          )}
                          {isRetailProduct && (
                            <Text fontSize="xs" color="gray.400" fontStyle="italic">
                              Gambar dikelola melalui variants
                            </Text>
                          )}
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onImagesClose}>Tutup</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Image Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteImageOpen}
        leastDestructiveRef={cancelImageRef}
        onClose={onDeleteImageClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Gambar
            </AlertDialogHeader>
            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus gambar <strong>{deleteImage?.BPPRODUCTIMAGE_NAME}</strong>?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelImageRef} onClick={onDeleteImageClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteImageConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Manage Stocks Modal */}
      <Modal isOpen={isStocksOpen} onClose={onStocksClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Kelola Stock - {managingStocksProduct?.BPPRODUCT_NAME}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <HStack>
                {isRetailProduct ? (
                  <Button
                    leftIcon={<FiPlus />}
                    onClick={() => handleCreateStock()}
                    colorScheme="brand"
                    size="sm"
                  >
                    Tambah Stock
                  </Button>
                ) : (
                  <Button
                    leftIcon={<FiPlus />}
                    onClick={handleCreateStock}
                    colorScheme="brand"
                    size="sm"
                  >
                    Tambah Stock
                  </Button>
                )}
              </HStack>
              <Divider />

              <Box>
                <Text fontSize="md" fontWeight="600" mb={3}>
                  Daftar Stock ({productStocks.length})
                </Text>
                {loadingStocks ? (
                  <VStack py={8}>
                    <Spinner size="lg" />
                    <Text color="gray.500">Memuat stock...</Text>
                  </VStack>
                ) : productStocks.length === 0 ? (
                  <Text py={8} color="gray.500" textAlign="center">
                    Belum ada stock untuk product ini
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {productStocks.map((stock) => (
                      <Box
                        key={stock.BPPRODUCTSTOCKS_ID}
                        p={4}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="8px"
                      >
                        <HStack justify="space-between" align="start">
                          <VStack align="start" spacing={2} flex={1}>
                            <Text fontSize="sm" fontWeight="600">
                              {stock.BPPRODUCTSTOCKS_LOCATIONNAME || '-'}
                            </Text>
                            {isRetailProduct && stock.BPPRODUCTVARIANT_KEY && (
                              <Text fontSize="xs" color="blue.600" fontWeight="600">
                                Variant Key: {stock.BPPRODUCTVARIANT_KEY}
                              </Text>
                            )}
                            {isRetailProduct && stock.BPPRODUCTSTOCKS_LOCATIONID && (
                              <Text fontSize="xs" color="gray.400">
                                Location ID: {stock.BPPRODUCTSTOCKS_LOCATIONID}
                              </Text>
                            )}
                            {isRetailProduct && stock.BPPRODUCTSTOCKS_PRICELISTNAME && (
                              <Text fontSize="xs" color="gray.400">
                                Pricelist: {stock.BPPRODUCTSTOCKS_PRICELISTNAME}
                              </Text>
                            )}
                            <Text fontSize="xs" color="gray.500">
                              Stock: {stock.BPPRODUCTSTOCKS_STOCK || '0'}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Price: {stock.BPPRODUCTSTOCKS_SELLPRICE || '-'}
                            </Text>
                          </VStack>
                          <HStack spacing={2}>
                            <IconButton
                              icon={<FiEdit />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleEditStock(stock)}
                              aria-label="Edit Stock"
                            />
                            <IconButton
                              icon={<FiTrash2 />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDeleteStockClick(stock)}
                              aria-label="Delete Stock"
                            />
                          </HStack>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onStocksClose}>Tutup</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Stock Form Modal */}
      <Modal isOpen={isStockFormOpen} onClose={onStockFormClose} size="md" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            {editingStock ? 'Edit Stock' : 'Tambah Stock Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {isRetailProduct ? (
                <>
                  <FormControl isRequired>
                    <FormLabel>Variant Key</FormLabel>
                    <Select
                      name="BPPRODUCTVARIANT_KEY"
                      value={stockFormData.BPPRODUCTVARIANT_KEY}
                      onChange={handleStockInputChange}
                      placeholder="Pilih Variant"
                      isReadOnly={!!editingStock}
                      bg={editingStock ? "gray.50" : "white"}
                    >
                      {productVariants.map((variant) => (
                        <option key={variant.BPPRODUCTVARIANT_KEY} value={variant.BPPRODUCTVARIANT_KEY}>
                          {variant.BPPRODUCTVARIANT_NAME || variant.BPPRODUCTVARIANT_KEY}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Location ID</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_LOCATIONID"
                      value={stockFormData.BPPRODUCTSTOCKS_LOCATIONID}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan Location ID"
                      type="text"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Location Name</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_LOCATIONNAME"
                      value={stockFormData.BPPRODUCTSTOCKS_LOCATIONNAME}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan nama lokasi"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Pricelist Name</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_PRICELISTNAME"
                      value={stockFormData.BPPRODUCTSTOCKS_PRICELISTNAME}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan nama pricelist"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Stock</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_STOCK"
                      value={stockFormData.BPPRODUCTSTOCKS_STOCK}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan jumlah stock"
                      type="number"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Sell Price</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_SELLPRICE"
                      value={stockFormData.BPPRODUCTSTOCKS_SELLPRICE}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan harga jual"
                      type="number"
                    />
                  </FormControl>
                </>
              ) : (
                <>
                  <FormControl isRequired>
                    <FormLabel>SKU</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_SKU"
                      value={stockFormData.BPPRODUCTSTOCKS_SKU}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan SKU"
                      isReadOnly={!!editingStock}
                      bg={editingStock ? "gray.50" : "white"}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Location Name</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_LOCATIONNAME"
                      value={stockFormData.BPPRODUCTSTOCKS_LOCATIONNAME}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan nama lokasi"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Stock</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_STOCK"
                      value={stockFormData.BPPRODUCTSTOCKS_STOCK}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan jumlah stock"
                      type="number"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Sell Price</FormLabel>
                    <Input
                      name="BPPRODUCTSTOCKS_SELLPRICE"
                      value={stockFormData.BPPRODUCTSTOCKS_SELLPRICE}
                      onChange={handleStockInputChange}
                      placeholder="Masukkan harga jual"
                      type="number"
                    />
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onStockFormClose}>
              Batal
            </Button>
            <Button colorScheme="brand" onClick={handleStockSubmit}>
              {editingStock ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Stock Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteStockOpen}
        leastDestructiveRef={cancelStockRef}
        onClose={onDeleteStockClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus Stock
            </AlertDialogHeader>
            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus stock di lokasi <strong>{deleteStock?.BPPRODUCTSTOCKS_LOCATIONNAME}</strong>?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelStockRef} onClick={onDeleteStockClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleDeleteStockConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Manage Variants Modal - Only for Retail Products */}
      {isRetailProduct && (
        <>
          <Modal isOpen={isVariantsOpen} onClose={onVariantsClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                Kelola Variants - {managingVariantsProduct?.BPPRODUCT_NAME}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Button
                      leftIcon={<FiPlus />}
                      onClick={handleCreateVariant}
                      colorScheme="brand"
                      size="sm"
                    >
                      Tambah Variant
                    </Button>
                  </HStack>

                  <Divider />

                  <Box>
                    <Text fontSize="md" fontWeight="600" mb={3}>
                      Daftar Variants ({productVariants.length})
                    </Text>
                    {loadingVariants ? (
                      <VStack py={8}>
                        <Spinner size="lg" />
                        <Text color="gray.500">Memuat variants...</Text>
                      </VStack>
                    ) : productVariants.length === 0 ? (
                      <Text py={8} color="gray.500" textAlign="center">
                        Belum ada variants untuk product ini
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={3}>
                        {productVariants.map((variant) => (
                          <Box
                            key={variant.BPPRODUCTVARIANT_ID}
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="8px"
                          >
                            <HStack justify="space-between" align="start">
                              <VStack align="start" spacing={2} flex={1}>
                                <Text fontSize="sm" fontWeight="600">
                                  {variant.BPPRODUCTVARIANT_NAME || variant.BPPRODUCTVARIANT_KEY}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Key: {variant.BPPRODUCTVARIANT_KEY}
                                </Text>
                                {variant.BPPRODUCTVARIANT_SKU && (
                                  <Text fontSize="xs" color="gray.500">
                                    SKU: {variant.BPPRODUCTVARIANT_SKU}
                                  </Text>
                                )}
                                {variant.BPPRODUCTVARIANT_BARCODE && (
                                  <Text fontSize="xs" color="gray.500">
                                    Barcode: {variant.BPPRODUCTVARIANT_BARCODE}
                                  </Text>
                                )}
                                {variant.BPPRODUCTVARIANT_BASE64 && (
                                  <Image
                                    src={`data:image/jpeg;base64,${variant.BPPRODUCTVARIANT_BASE64}`}
                                    alt={variant.BPPRODUCTVARIANT_NAME || 'Variant image'}
                                    maxH="100px"
                                    maxW="150px"
                                    objectFit="contain"
                                    borderRadius="4px"
                                  />
                                )}
                                {variant.stocks && variant.stocks.length > 0 && (
                                  <Text fontSize="xs" color="gray.400">
                                    Stocks: {variant.stocks.length} location(s)
                                  </Text>
                                )}
                                {variant.attributes && variant.attributes.length > 0 && (
                                  <Text fontSize="xs" color="gray.400">
                                    Attributes: {variant.attributes.length}
                                  </Text>
                                )}
                              </VStack>
                              <HStack spacing={2}>
                                <IconButton
                                  icon={<FiEdit />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => handleEditVariant(variant)}
                                  aria-label="Edit Variant"
                                />
                                <IconButton
                                  icon={<FiPackage />}
                                  size="sm"
                                  colorScheme="purple"
                                  variant="ghost"
                                  onClick={() => handleManageAttributes(variant)}
                                  aria-label="Manage Attributes"
                                  title="Manage Attributes"
                                />
                                <IconButton
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => handleDeleteVariantClick(variant)}
                                  aria-label="Delete Variant"
                                />
                              </HStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onVariantsClose}>Tutup</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Variant Form Modal */}
          <Modal isOpen={isVariantFormOpen} onClose={onVariantFormClose} size="md" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxH="90vh">
              <ModalHeader>
                {editingVariant ? 'Edit Variant' : 'Tambah Variant Baru'}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Variant Key</FormLabel>
                    <Input
                      name="BPPRODUCTVARIANT_KEY"
                      value={variantFormData.BPPRODUCTVARIANT_KEY}
                      onChange={handleVariantInputChange}
                      placeholder="Masukkan Variant Key"
                      isReadOnly={!!editingVariant}
                      bg={editingVariant ? "gray.50" : "white"}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Variant Name</FormLabel>
                    <Input
                      name="BPPRODUCTVARIANT_NAME"
                      value={variantFormData.BPPRODUCTVARIANT_NAME}
                      onChange={handleVariantInputChange}
                      placeholder="Masukkan nama variant"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Barcode</FormLabel>
                    <Input
                      name="BPPRODUCTVARIANT_BARCODE"
                      value={variantFormData.BPPRODUCTVARIANT_BARCODE}
                      onChange={handleVariantInputChange}
                      placeholder="Masukkan barcode"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>SKU</FormLabel>
                    <Input
                      name="BPPRODUCTVARIANT_SKU"
                      value={variantFormData.BPPRODUCTVARIANT_SKU}
                      onChange={handleVariantInputChange}
                      placeholder="Masukkan SKU"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Product Key</FormLabel>
                    <Input
                      name="BPPRODUCT_KEY"
                      value={variantFormData.BPPRODUCT_KEY}
                      onChange={handleVariantInputChange}
                      placeholder="Masukkan Product Key"
                      isReadOnly
                      bg="gray.50"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Gambar (Base64)</FormLabel>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleVariantImageChange}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Format: JPG, PNG, GIF. Maksimal 5MB.
                    </Text>
                    {variantFormData.BPPRODUCTVARIANT_BASE64 && (
                      <Box mt={2}>
                        <Image
                          src={`data:image/jpeg;base64,${variantFormData.BPPRODUCTVARIANT_BASE64}`}
                          alt="Preview"
                          maxH="150px"
                          maxW="200px"
                          objectFit="contain"
                          borderRadius="4px"
                          border="1px solid"
                          borderColor="gray.200"
                        />
                      </Box>
                    )}
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onVariantFormClose} mr={3}>
                  Batal
                </Button>
                <Button colorScheme="brand" onClick={handleVariantSubmit}>
                  {editingVariant ? 'Update' : 'Simpan'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Delete Variant Confirmation Dialog */}
          <AlertDialog
            isOpen={isDeleteVariantOpen}
            leastDestructiveRef={cancelVariantRef}
            onClose={onDeleteVariantClose}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Hapus Variant
                </AlertDialogHeader>
                <AlertDialogBody>
                  Apakah Anda yakin ingin menghapus variant <strong>{deleteVariant?.BPPRODUCTVARIANT_NAME || deleteVariant?.BPPRODUCTVARIANT_KEY}</strong>?
                  <br />
                  <br />
                  <Text fontSize="sm" color="red.500">
                    Peringatan: Tindakan ini akan menghapus variant beserta semua attributes dan stocks yang terkait secara permanen.
                  </Text>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelVariantRef} onClick={onDeleteVariantClose}>
                    Batal
                  </Button>
                  <Button colorScheme="red" onClick={handleDeleteVariantConfirm} ml={3}>
                    Hapus
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

          {/* Manage Attributes Modal */}
          <Modal isOpen={isAttributesOpen} onClose={onAttributesClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                Kelola Attributes - {managingAttributesVariant?.BPPRODUCTVARIANT_NAME || managingAttributesVariant?.BPPRODUCTVARIANT_KEY}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Button
                      leftIcon={<FiPlus />}
                      onClick={handleCreateAttribute}
                      colorScheme="brand"
                      size="sm"
                    >
                      Tambah Attribute
                    </Button>
                  </HStack>

                  <Divider />

                  <Box>
                    <Text fontSize="md" fontWeight="600" mb={3}>
                      Daftar Attributes ({variantAttributes.length})
                    </Text>
                    {loadingAttributes ? (
                      <VStack py={8}>
                        <Spinner size="lg" />
                        <Text color="gray.500">Memuat attributes...</Text>
                      </VStack>
                    ) : variantAttributes.length === 0 ? (
                      <Text py={8} color="gray.500" textAlign="center">
                        Belum ada attributes untuk variant ini
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={3}>
                        {variantAttributes.map((attribute) => (
                          <Box
                            key={attribute.BPPRODUCTATTR_ID}
                            p={4}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="8px"
                          >
                            <HStack justify="space-between" align="start">
                              <VStack align="start" spacing={2} flex={1}>
                                <Text fontSize="sm" fontWeight="600">
                                  {attribute.BPPRODUCTATTR_DISPLAYNAME || attribute.BPPRODUCTATTR_ATTRIBUTE || '-'}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Attribute: {attribute.BPPRODUCTATTR_ATTRIBUTE || '-'}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Value: {attribute.BPPRODUCTATTR_VALUE || '-'}
                                </Text>
                                {attribute.BPPRODUCTATTR_DISPLAYNAME && (
                                  <Text fontSize="xs" color="gray.400">
                                    Display Name: {attribute.BPPRODUCTATTR_DISPLAYNAME}
                                  </Text>
                                )}
                              </VStack>
                              <HStack spacing={2}>
                                <IconButton
                                  icon={<FiEdit />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => handleEditAttribute(attribute)}
                                  aria-label="Edit Attribute"
                                />
                                <IconButton
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => handleDeleteAttributeClick(attribute)}
                                  aria-label="Delete Attribute"
                                />
                              </HStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onAttributesClose}>Tutup</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Attribute Form Modal */}
          <Modal isOpen={isAttributeFormOpen} onClose={onAttributeFormClose} size="md" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxH="90vh">
              <ModalHeader>
                {editingAttribute ? 'Edit Attribute' : 'Tambah Attribute Baru'}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Attribute</FormLabel>
                    <Input
                      name="BPPRODUCTATTR_ATTRIBUTE"
                      value={attributeFormData.BPPRODUCTATTR_ATTRIBUTE}
                      onChange={handleAttributeInputChange}
                      placeholder="Masukkan attribute (contoh: Color, Size)"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Value</FormLabel>
                    <Input
                      name="BPPRODUCTATTR_VALUE"
                      value={attributeFormData.BPPRODUCTATTR_VALUE}
                      onChange={handleAttributeInputChange}
                      placeholder="Masukkan value (contoh: Red, Large)"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Display Name</FormLabel>
                    <Input
                      name="BPPRODUCTATTR_DISPLAYNAME"
                      value={attributeFormData.BPPRODUCTATTR_DISPLAYNAME}
                      onChange={handleAttributeInputChange}
                      placeholder="Masukkan display name (contoh: Red - Large)"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Variant Key</FormLabel>
                    <Input
                      name="BPPRODUCTVARIANT_KEY"
                      value={attributeFormData.BPPRODUCTVARIANT_KEY}
                      onChange={handleAttributeInputChange}
                      placeholder="Variant Key"
                      isReadOnly
                      bg="gray.50"
                    />
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onAttributeFormClose} mr={3}>
                  Batal
                </Button>
                <Button colorScheme="brand" onClick={handleAttributeSubmit}>
                  {editingAttribute ? 'Update' : 'Simpan'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Delete Attribute Confirmation Dialog */}
          <AlertDialog
            isOpen={isDeleteAttributeOpen}
            leastDestructiveRef={cancelAttributeRef}
            onClose={onDeleteAttributeClose}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Hapus Attribute
                </AlertDialogHeader>
                <AlertDialogBody>
                  Apakah Anda yakin ingin menghapus attribute <strong>{deleteAttribute?.BPPRODUCTATTR_DISPLAYNAME || deleteAttribute?.BPPRODUCTATTR_ATTRIBUTE}</strong>?
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelAttributeRef} onClick={onDeleteAttributeClose}>
                    Batal
                  </Button>
                  <Button colorScheme="red" onClick={handleDeleteAttributeConfirm} ml={3}>
                    Hapus
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      )}
    </>
  );
};

export default BundlingProductTab;

